import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import DataTable from "react-data-table-component";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import * as XLSX from "xlsx";
import $ from "jquery";
import Swal from "sweetalert2";
import "datatables.net";
import "datatables.net-buttons";
import "datatables.net-buttons/js/buttons.html5";
import "datatables.net-buttons/js/buttons.print";
import "datatables.net-buttons/js/buttons.colVis";
import "datatables.net-buttons-dt/css/buttons.dataTables.min.css";
import { baseurl } from "../utility/helper";
import "../assets/css/CandidatePortal.css";
import defaultImage from "../assets/image/demo.jpeg";

class WidgetErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(err, info) { console.error("Widget crashed:", err, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 16, color: "var(--cl-danger,#ef4444)", fontSize: 13 }}>
          ⚠ This section failed to load. Other tables are unaffected.
        </div>
      );
    }
    return this.props.children;
  }
}
// ─────────────────────────────────────────────────────────────────────────────
// API
// ─────────────────────────────────────────────────────────────────────────────
const API = {
  candidates:      `${baseurl}/admin/candidates`,
  stats:           `${baseurl}/admin/candidates/stats`,
  candidate:       (id) => `${baseurl}/admin/candidates/${id}`,
  bulk:            `${baseurl}/admin/candidates/bulk`,
  tests:           `${baseurl}/admin/tests`,
  instituteTests:  `${baseurl}/admin/institutetests`,
  unregister:      (id) => `${baseurl}/admin/candidates/${id}/unregister`,
  deactivate:      (id) => `${baseurl}/admin/candidates/${id}/deactivate`,
  assignTest:      `${baseurl}/admin/candidates/assign-test`,
  bulkAssignTest:  `${baseurl}/admin/candidates/bulk-assign-test`,
  checkDuplicate:  `${baseurl}/admin/candidates/check-duplicate`,
  checkId:         `${baseurl}/admin/candidates/check-id`,
};

// ─────────────────────────────────────────────────────────────────────────────
// ID PROOF TYPES — single source of truth for type + validation length
// ─────────────────────────────────────────────────────────────────────────────
const ID_TYPES = [
  { value: "aadhar",   label: "Aadhar (Last 4 digits)", maxLength: 4,  hint: "Enter last 4 digits" },
  { value: "pan",      label: "PAN Card",                maxLength: 10, hint: "e.g. ABCDE1234F" },
  { value: "dl",       label: "Driving Licence",         maxLength: 16, hint: "Enter DL number" },
  { value: "pass", label: "Passport",                maxLength: 9,  hint: "e.g. A1234567" },
  { value: "na",       label: "Not Available",           maxLength: 0,  hint: "" },
];
const idTypeMeta = (idType) => ID_TYPES.find((t) => t.value === idType) || ID_TYPES[0];

const getToken    = () => sessionStorage.getItem("token") || "";
const authHeaders = () => ({ Authorization: getToken(), "Content-Type": "application/json" });
const getRoleId   = () => parseInt(sessionStorage.getItem("userroleId") || "0");

// ─────────────────────────────────────────────────────────────────────────────
// AGE HELPER (DOB-based eligibility — must be >= 1 yr old)
// ─────────────────────────────────────────────────────────────────────────────
function calcAgeInYears(dob) {
  if (!dob) return null;
  const birth = new Date(dob);
  if (isNaN(birth)) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

function formatDateTime(dt) {
  if (!dt) return "";
  const d = new Date(dt);
  if (isNaN(d)) return dt;
  return d.toLocaleString("en-IN", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function isEligible(dob) {
  const age = calcAgeInYears(dob);
  return age === null || age >= 1;
}

// ─────────────────────────────────────────────────────────────────────────────
// TEST DATE ELIGIBILITY HELPERS
// Rule: if candidate has a previous test_date, at least 1 year must have
//       passed since that date before they can be assigned a new test.
// ─────────────────────────────────────────────────────────────────────────────
function isTestEligible(candidate) {
  if (candidate.testStatus === "unregistered") return true;
  const testDate = candidate.testDate ?? "";
  if (!testDate) return true;
  const taken = new Date(testDate);
  if (isNaN(taken)) return true;
  const oneYearLater = new Date(taken);
  oneYearLater.setFullYear(oneYearLater.getFullYear() + 1);
  return new Date() >= oneYearLater;
}

function daysUntilEligible(candidate) {
  if (candidate.testStatus === "unregistered") return 0;
  const testDate = candidate.testDate ?? "";
  if (!testDate) return 0;
  const taken = new Date(testDate);
  if (isNaN(taken)) return 0;
  const oneYearLater = new Date(taken);
  oneYearLater.setFullYear(oneYearLater.getFullYear() + 1);
  const diff = oneYearLater - new Date();
  return diff > 0 ? Math.ceil(diff / (1000 * 60 * 60 * 24)) : 0;
}

// ─────────────────────────────────────────────────────────────────────────────
// NORMALISERS
// ─────────────────────────────────────────────────────────────────────────────
const normaliseTests = (raw) => {
  const list = Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []);
  return list.map((t) =>
    typeof t === "string" ? { id: t, name: t } : {
      id:   String(t.test_id ?? t.id ?? t._id ?? t.value ?? ""),
      name: t.test_title ?? t.name ?? t.label ?? t.title ?? "",
    }
  );
};

// Institute (batch) tests, labelled with their institute / batch so Super Admin
// can tell them apart from chapter tests in the same dropdown.
const normaliseInstituteTests = (raw) =>
  (Array.isArray(raw) ? raw : []).map((t) => ({
    id:        String(t.test_id),
    name:      `${t.test_title}${t.institute_name ? ` — ${t.institute_name}` : ""}${t.batch_name ? ` / ${t.batch_name}` : ""}`,
    institute: true,
  }));

// <option>s for a test <select>; institute tests go in their own group.
function TestOptions({ tests }) {
  const chapterTests   = tests.filter((t) => !t.institute);
  const instituteTests = tests.filter((t) =>  t.institute);
  if (!instituteTests.length) {
    return chapterTests.map((t) => <option key={t.id} value={t.id}>{t.name}</option>);
  }
  return (
    <>
      <optgroup label="Chapter Tests">
        {chapterTests.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
      </optgroup>
      <optgroup label="Institute Tests">
        {instituteTests.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
      </optgroup>
    </>
  );
}

const normaliseRow = (c) => ({
  id:           c.candidate_id  ?? c.id ?? "",
  firstName:    c.first_name    ?? "",
  lastName:     c.last_name     ?? "",
  candidate_img:(c.candidate_img ?? "").replace(/[\r\n\s]/g, ""),
  email:        c.email         ?? "",
  mobile:       c.mobile_no     ?? c.mobile ?? "",
  gender:       c.gender        ?? "",
  dob:          c.dob           ?? "",
  address:      c.address       ?? "",
  city:         c.city          ?? "",
  state:        c.state         ?? "",
  pincode:      c.pincode       ?? "",
  test:         c.test_title    ?? c.test  ?? "",
  testId:       String(c.test_id  ?? ""),
  testDate:     c.registered_at ?? "",
  joiningDate:  c.joining_date ?? "",
  amount:       String(c.amount   ?? ""),
  status:       (c.candidate_status ?? "active").toLowerCase(),
  testStatus:   (c.test_status ?? "").toLowerCase(),
  // ── Generic ID proof — idType tells you what idNumber means ──────────────
  idType:       c.id_type   ?? "aadhar",
  idNumber:     c.aadhar_last ?? "",
  hasTest:      !!(c.test_id || c.test_title),
});

// ─────────────────────────────────────────────────────────────────────────────
// DUPLICATE CHECK HELPERS
// ─────────────────────────────────────────────────────────────────────────────
async function checkNameDobDuplicate({ firstName, lastName, dob, excludeId = null }) {
  try {
    const res = await axios.post(
      API.checkDuplicate,
      { first_name: firstName, last_name: lastName, dob, exclude_id: excludeId },
      { headers: authHeaders() }
    );
    if (res.data?.duplicate) {
      return "A candidate with the same first name, last name, and date of birth already exists.";
    }
    return null;
  } catch (e) {
    console.warn("Duplicate name/DOB check failed:", e.message);
    return null;
  }
}

// Generic — replaces the old aadhar-only check. Skips check for "na" or empty.
async function checkIdDuplicate({ idType, idNumber, excludeId = null }) {
  if (!idType || idType === "na" || !idNumber) return null;
  try {
    const res = await axios.post(
      API.checkId,
      { id_type: idType, aadhar_last: idNumber, exclude_id: excludeId },
      { headers: authHeaders() }
    );
    if (res.data?.duplicate) {
      const label = idTypeMeta(idType).label;
      return `${label} number "${idNumber}" is already registered to another candidate.`;
    }
    return null;
  } catch (e) {
    console.warn("ID duplicate check failed:", e.message);
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// BULK UPLOAD HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const BULK_HEADERS = [
  "first_name","last_name","email","mobile","gender",
  "dob","address","city","state","pincode","candidate_img","id_type","aadhar_last",
];
const SAMPLE_ROWS = [
  ["Rahul","Sharma","rahul.sharma@email.com","9876543210","male","15-08-1998","123 MG Road","Mumbai","Maharashtra","400001","","aadhar","1234"],
  ["Priya","Patel","priya.patel@email.com","9123456780","female","22-03-2000","45 Gandhi Nagar","Ahmedabad","Gujarat","380007","","pan","ABCDE1234F"],
  ["Amit","Verma","amit.verma@email.com","9988776655","male","10-12-1995","Plot 7 Sector 18","Noida","Uttar Pradesh","201301","","na",""],
];

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  Object.assign(document.createElement("a"), { href: url, download: filename }).click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function downloadSampleExcel() {
  const ws = XLSX.utils.aoa_to_sheet([BULK_HEADERS, ...SAMPLE_ROWS]);
  ws["!cols"] = BULK_HEADERS.map((h) => ({ wch: Math.max(h.length + 4, 16) }));
  const dobColIdx = BULK_HEADERS.indexOf("dob");
  const range = XLSX.utils.decode_range(ws["!ref"]);
  for (let R = range.s.r + 1; R <= range.e.r; R++) {
    const cellAddr = XLSX.utils.encode_cell({ r: R, c: dobColIdx });
    if (!ws[cellAddr]) continue;
    const raw = ws[cellAddr].v;
    if (raw) {
      const [d, m, y] = String(raw).split("-").map(Number);
      const date = new Date(y, m - 1, d);
      if (!isNaN(date)) {
        const excelSerial = Math.floor((date - new Date(1899, 11, 30)) / 86400000);
        ws[cellAddr] = { t: "n", v: excelSerial, z: "DD-MM-YYYY" };
      }
    }
  }
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Candidates");
  const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  triggerDownload(
    new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
    "candidate_profile_sample.xlsx"
  );
}

function compressImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.onload  = (e) => {
      const img = new Image();
      img.onerror = () => reject(new Error("Could not load image"));
      img.onload  = () => {
        const MAX   = 200;
        const ratio = Math.min(MAX / img.width, MAX / img.height, 1);
        const w     = Math.round(img.width  * ratio);
        const h     = Math.round(img.height * ratio);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", 0.8));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

function normaliseDob(raw) {
  if (!raw) return "";
  if (typeof raw === "number") return excelSerialToISO(raw);
  const s = String(raw).trim();
  if (/^\d{2}-\d{2}-\d{4}$/.test(s)) { const [d,m,y]=s.split("-"); return `${y}-${m}-${d}`; }
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) { const [d,m,y]=s.split("/"); return `${y}-${m}-${d}`; }
  return s;
}
function excelSerialToISO(serial) {
  const date = new Date(Math.round((serial - 25569) * 86400 * 1000));
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  const d = String(date.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

// Generic ID sanitiser — used both on manual entry and bulk-excel parsing.
function sanitiseIdNumber(idType, raw) {
  const s = String(raw ?? "");

  if (idType === "na") return "";

  if (idType === "aadhar") {
    return s.replace(/\D/g, "").slice(0, 4);
  }

  return s.toUpperCase().slice(0, idTypeMeta(idType).maxLength);
}

function parseExcel(buffer) {
  const wb      = XLSX.read(buffer, { type: "array" });
  const ws      = wb.Sheets[wb.SheetNames[0]];
  const rawRows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
  if (rawRows.length < 2) return [];
  const headers = rawRows[0].map((h) => String(h).toLowerCase().trim());
  const idx     = {};
  BULK_HEADERS.forEach((h) => { idx[h] = headers.indexOf(h); });
  return rawRows.slice(1).map((cols, i) => {
    const row = {};
    BULK_HEADERS.forEach((h) => {
      const raw = idx[h] >= 0 ? cols[idx[h]] : "";
      row[h] = h === "dob" ? normaliseDob(raw) : String(raw ?? "").trim();
    });
    row.id_type   = (row.id_type || "aadhar").toLowerCase();
    if (!ID_TYPES.some((t) => t.value === row.id_type)) row.id_type = "aadhar";
    row.aadhar_last = sanitiseIdNumber(row.id_type, row.aadhar_last);
    row._rowIdx = i;
    row._error  = validateRow(row);
    return row;
  }).filter((r) => Object.values(r).some((v) => v && v !== r._error && v !== r._rowIdx));
}

function validateRow(row) {
  if (!row.first_name || !row.last_name)                                   return "Missing name";
  // if (!row.email || !/\S+@\S+\.\S+/.test(row.email))                      return "Invalid email";
  // if (!row.mobile || row.mobile.replace(/\D/g, "").length < 10)           return "Invalid mobile";
  // if (!["male","female","other"].includes((row.gender||"").toLowerCase())) return "Invalid gender";
  // if (row.dob && !isEligible(row.dob))                                     return "Age < 1 yr — ineligible";
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// EMPTY FORMS
// ─────────────────────────────────────────────────────────────────────────────
const EMPTY_PROFILE_FORM = {
  firstName:"", lastName:"", email:"", mobile:"", gender:"", dob:"",
  address:"", city:"", state:"", pincode:"",
  image: null,
  idType: "aadhar",
  idNumber: "",
};

// ─────────────────────────────────────────────────────────────────────────────
// SHARED UI PRIMITIVES
// ─────────────────────────────────────────────────────────────────────────────
function GenderBadge({ gender }) {
  const cls = gender === "male" ? "badge-m" : gender === "female" ? "badge-f" : "badge-o";
  return <span className={`cl-badge ${cls}`}>{gender}</span>;
}
function StatusBadge({ status }) {
  const map = {
    active:       { cls:"badge-ok",  label:"Active" },
    deactivated:  { cls:"badge-err", label:"Deactivated" },
    unregistered: { cls:"badge-o",   label:"Unregistered" },
  };
  const { cls, label } = map[(status||"active").toLowerCase()] || map.active;
  return <span className={`cl-badge ${cls}`}>{label}</span>;
}
function Toast({ message, type, onClose }) {
  useEffect(() => { if (!message) return; const t = setTimeout(onClose, 3500); return () => clearTimeout(t); }, [message, onClose]);
  if (!message) return null;
  return <div className={`cl-toast cl-toast-${type}`}><span>{message}</span><button className="cl-toast-close" onClick={onClose}>✕</button></div>;
}
function ConfirmModal({ open, title, message, confirmLabel="Confirm", confirmClass="cl-btn-danger", onConfirm, onCancel }) {
  if (!open) return null;
  return (
    <div className="cl-modal-overlay" onClick={onCancel}>
      <div className="cl-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cl-modal-header"><span className="cl-modal-title">{title}</span><button className="cl-modal-close" onClick={onCancel}>✕</button></div>
        <div className="cl-modal-body">{message}</div>
        <div className="cl-modal-footer">
          <button className="cl-btn cl-btn-ghost" onClick={onCancel}>Cancel</button>
          <button className={`cl-btn ${confirmClass}`} onClick={onConfirm}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}
function SectionTitle({ children }) { return <div className="cl-sec-title"><span className="cl-sec-bar"/>{children}</div>; }
function Field({ label, required, children, full }) {
  return <div className={`cl-field${full?" cl-field-full":""}`}><label className="cl-label">{label}{required&&<span className="cl-req">*</span>}</label>{children}</div>;
}
function FInput({ value, onChange, placeholder, type="text", maxLength, disabled, hasError }) {
  return <input type={type} value={value} maxLength={maxLength} disabled={disabled} placeholder={placeholder} className={`cl-input${hasError?" cl-input-err":""}`} onChange={(e) => onChange(e.target.value)}/>;
}
function FSelect({ value, onChange, disabled, children }) {
  return <div className="cl-sel-wrap"><select value={value} disabled={disabled} className="cl-select" onChange={(e) => onChange(e.target.value)}>{children}</select><span className="cl-sel-arrow">▾</span></div>;
}
function Card({ children, style }) { return <div className="cl-card" style={style}>{children}</div>; }
function InlineError({ msg }) { if (!msg) return null; return <div className="cl-form-error">⚠ {msg}</div>; }
function StatCard({ label, value, active, onClick }) {
  return <div className={`cl-stat${active?" cl-stat-active":""}${onClick?" cl-stat-clickable":""}`} onClick={onClick}><div className="cl-stat-label"><p>{label}</p></div><div className="cl-stat-val">{value ?? "—"}</div></div>;
}
// Small shared display helper for "ID Proof" cells across tables
function idProofDisplay(idType, idNumber) {
  if (!idNumber) return "—";
  const label = idTypeMeta(idType).label.split(" (")[0];
  return `${label}: ${idNumber}`;
}

// Amount column often comes back as a numeric-string from Postgres
// (e.g. "1000.00") — show whole rupee amounts without the trailing
// ".00" instead of mixing "₹1000" and "₹1000.00" in the same table.
function formatAmount(value) {
  if (value === null || value === undefined || value === "") return null;
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return num % 1 === 0 ? String(num) : num.toFixed(2);
}

// ─────────────────────────────────────────────────────────────────────────────
// ASSIGN TEST MODAL
// ─────────────────────────────────────────────────────────────────────────────
function AssignTestModal({ open, candidates, tests, onClose, onSuccess }) {
  const [testId, setTestId] = useState("");
  const [amount, setAmount] = useState("");
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState("");

  useEffect(() => { if (open) { setTestId(""); setAmount(""); setError(""); } }, [open]);

  if (!open) return null;

  const eligible   = candidates.filter((c) =>  isTestEligible(c));
  const ineligible = candidates.filter((c) => !isTestEligible(c));

  const submit = async () => {
    if (!eligible.length)         { setError("No eligible candidates to assign a test to."); return; }
    if (!testId)                  { setError("Please select a test."); return; }
    if (!amount || isNaN(amount)) { setError("Please enter a valid amount."); return; }
    setSaving(true); setError("");
    try {
      await axios.post(
        API.assignTest,
        { candidate_ids: eligible.map((c) => c.id), test_id: testId, amount },
        { headers: authHeaders() }
      );
      onSuccess(`✓ Test assigned to ${eligible.length} candidate${eligible.length !== 1 ? "s" : ""}!`);
      onClose();
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Failed to assign test");
    } finally { setSaving(false); }
  };

  const testName = tests.find((t) => t.id === testId)?.name || "";

  return (
    <div className="cl-modal-overlay" onClick={onClose}>
      <div className="cl-modal" style={{ maxWidth: 540 }} onClick={(e) => e.stopPropagation()}>
        <div className="cl-modal-header">
          <span className="cl-modal-title">🎯 Assign Test</span>
          <button className="cl-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="cl-modal-body">

          {eligible.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "var(--cl-label)", marginBottom: 6, letterSpacing: "0.06em" }}>
                ✓ Eligible — {eligible.length} candidate{eligible.length !== 1 ? "s" : ""}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {eligible.slice(0, 10).map((c) => (
                  <span key={c.id} className="cl-badge badge-ok" style={{ fontSize: 11 }}>
                    {c.firstName} {c.lastName}
                  </span>
                ))}
                {eligible.length > 10 && (
                  <span className="cl-badge badge-o" style={{ fontSize: 11 }}>+{eligible.length - 10} more</span>
                )}
              </div>
            </div>
          )}

          {ineligible.length > 0 && (
            <div style={{
              marginBottom: 14, padding: "10px 12px",
              background: "var(--cl-danger-bg,#fef2f2)",
              border: "1px solid var(--cl-danger,#ef4444)",
              borderRadius: 8,
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--cl-danger,#ef4444)", marginBottom: 6 }}>
                ⛔ {ineligible.length} candidate{ineligible.length !== 1 ? "s" : ""} skipped — 1-year gap since last test not yet completed
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {ineligible.map((c) => (
                  <span key={c.id} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                    <span className="cl-badge badge-err" style={{ fontSize: 11 }}>{c.firstName} {c.lastName}</span>
                    <span className="cl-muted" style={{ fontSize: 10 }}>({daysUntilEligible(c)}d left)</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {eligible.length === 0 ? (
            <div style={{
              padding: "18px 16px", textAlign: "center",
              color: "var(--cl-danger,#ef4444)", fontWeight: 600, fontSize: 13,
              background: "var(--cl-danger-bg,#fef2f2)", borderRadius: 8,
            }}>
              ⛔ None of the selected candidates are eligible yet.<br/>
              <span style={{ fontSize: 12, fontWeight: 400, color: "var(--cl-label)" }}>
                Each candidate must wait 1 year after their last test date before being assigned a new test.
              </span>
            </div>
          ) : (
            <>
              <div className="cl-form-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
                <Field label="Test" required>
                  <FSelect value={testId} onChange={setTestId}>
                    <option value="">— Select test —</option>
                    <TestOptions tests={tests} />
                  </FSelect>
                </Field>
                <Field label="Amount (₹)" required>
                  <FInput type="number" value={amount} onChange={setAmount} placeholder="0.00" />
                </Field>
              </div>
              {testName && amount && (
                <div style={{ marginTop: 8, padding: "8px 12px", background: "var(--cl-success-bg,#f0fdf4)", borderRadius: 8, fontSize: 12, color: "var(--cl-success,#10b981)", fontWeight: 500 }}>
                  ✓ Will assign <strong>{testName}</strong> for ₹{amount} to {eligible.length} eligible candidate{eligible.length !== 1 ? "s" : ""}
                </div>
              )}
            </>
          )}

          {error && <InlineError msg={error} />}
        </div>
        <div className="cl-modal-footer">
          <button className="cl-btn cl-btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            className="cl-btn cl-btn-primary"
            onClick={submit}
            disabled={saving || eligible.length === 0}
          >
            {saving ? "⏳ Assigning…" : `🎯 Assign to ${eligible.length} Eligible`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// INELIGIBLE CANDIDATES PANEL
// ─────────────────────────────────────────────────────────────────────────────
function IneligiblePanel({ onReload }) {
  const [rows,      setRows]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [photos,    setPhotos]    = useState({});
  const [uploading, setUploading] = useState({});
  const [toasts,    setToasts]    = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(API.candidates, {
        headers: authHeaders(),
        params: { start: 0, length: 9999, draw: 1 },
      });
      const all = (res.data.data || []).map(normaliseRow);
      setRows(all.filter((r) => !isEligible(r.dob)));
    } catch (e) {
      console.error("Ineligible fetch failed", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handlePhoto = useCallback(async (id, file) => {
    if (!file) return;
    try {
      const compressed = await compressImage(file);
      setPhotos((p) => ({ ...p, [id]: compressed }));
    } catch (e) { console.warn("Compress failed", e); }
  }, []);

  // ── FIX: this used to reference `form.idType`/`form.idNumber`, which do
  // not exist in this component's scope — it used `row` throughout. ─────────
  const handleUpload = useCallback(async (row) => {
    const photo = photos[row.id];
    setUploading((u) => ({ ...u, [row.id]: true }));
    try {
      const fd = new FormData();
      fd.append("first_name",  row.firstName);
      fd.append("last_name",   row.lastName);
      fd.append("email",       row.email);
      fd.append("mobile_no",   row.mobile);
      fd.append("gender",      row.gender);
      fd.append("dob",         row.dob);
      fd.append("address",     row.address || "");
      fd.append("city",        row.city    || "");
      fd.append("state",       row.state   || "");
      fd.append("pincode",     row.pincode || "");
      fd.append("id_type",     row.idType   || "aadhar");
      fd.append("aadhar_last",   row.idType === "na" ? "" : (row.idNumber || ""));
      if (photo) fd.append("candidate_img", photo);
      await axios.put(API.candidate(row.id), fd, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      });
      setToasts((t) => ({ ...t, [row.id]: { msg: "✓ Updated!", type: "success" } }));
      setTimeout(() => setToasts((t) => { const n={...t}; delete n[row.id]; return n; }), 3000);
      onReload?.();
    } catch (e) {
      setToasts((t) => ({ ...t, [row.id]: { msg: e.response?.data?.error || "Failed", type: "error" } }));
    } finally {
      setUploading((u) => ({ ...u, [row.id]: false }));
    }
  }, [photos, onReload]);

  const dtCols = [
    { name:"#", width:"48px", cell:(_,i)=><span className="cl-muted">{i+1}</span> },
    { name:"Candidate", grow:2, cell:(r)=>(<div><div style={{fontWeight:600}}>{r.firstName} {r.lastName}</div><div className="cl-muted" style={{fontSize:11}}>{r.email}</div></div>) },
    { name:"Mobile", selector:(r)=>r.mobile, width:"120px" },
    { name:"Gender", cell:(r)=><GenderBadge gender={r.gender}/>, width:"90px" },
    { name:"DOB / Age", width:"130px", cell:(r)=>{ const age=calcAgeInYears(r.dob); return (<div><div style={{fontSize:12}}>{r.dob}</div><span className="cl-badge badge-err" style={{fontSize:10}}>{age===null?"Unknown age":age<0?"Invalid DOB":`${age} yr — ineligible`}</span></div>); } },
    { name:"ID Proof", width:"140px", cell:(r)=>(<span className="cl-muted" style={{fontFamily:"monospace",fontSize:11}}>{idProofDisplay(r.idType, r.idNumber)}</span>) },
    { name:"Photo", width:"110px", cell:(r)=>{ const img=photos[r.id]; return img?(<div style={{display:"flex",alignItems:"center",gap:4}}><img src={img} alt="" width="34" height="34" style={{borderRadius:"50%",objectFit:"cover",border:"2px solid var(--cl-border)"}}/><button title="Remove" onClick={()=>setPhotos((p)=>{const n={...p};delete n[r.id];return n;})} style={{background:"none",border:"none",cursor:"pointer",color:"var(--cl-danger,#ef4444)",fontSize:14,padding:2}}>✕</button></div>):(<label style={{display:"flex",alignItems:"center",gap:4,cursor:"pointer",fontSize:11,color:"var(--cl-primary,#6366f1)",userSelect:"none"}}><span style={{fontSize:18,lineHeight:1}}>📷</span><span>Add</span><input type="file" accept="image/*" style={{display:"none"}} onChange={(e)=>handlePhoto(r.id,e.target.files[0])}/></label>); } },
    { name:"Action", width:"130px", cell:(r)=>{ const t=toasts[r.id]; if(t) return <span className={`cl-badge ${t.type==="success"?"badge-ok":"badge-err"}`} style={{fontSize:10}}>{t.msg}</span>; return (<button className="cl-btn cl-btn-primary cl-btn-sm" disabled={uploading[r.id]} onClick={()=>handleUpload(r)}>{uploading[r.id]?"⏳…":"⬆ Upload"}</button>); } },
  ];

  const previewStyles = {
    table:     { style:{ background:"transparent", fontSize:"12px" } },
    headRow:   { style:{ background:"var(--cl-th-bg)", borderBottomColor:"var(--cl-border)", minHeight:"36px" } },
    headCells: { style:{ fontSize:"10px", fontWeight:700, color:"var(--cl-label)", letterSpacing:"0.06em", padding:"0 10px" } },
    rows:      { style:{ fontSize:"12px", borderBottomColor:"var(--cl-border)", minHeight:"44px" } },
    cells:     { style:{ padding:"0 10px" } },
    pagination:{ style:{ fontSize:"12px", color:"var(--cl-label)", borderTopColor:"var(--cl-border)" } },
  };

  if (loading || !rows.length) return null;

  return (
    <div className="profile-section" style={{marginTop:24}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",background:"var(--cl-danger-bg,#fef2f2)",border:"1px solid var(--cl-danger,#ef4444)",borderRadius:10,padding:"10px 16px",marginBottom:12}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <span style={{fontSize:22}}>⚠️</span>
          <div>
            <div style={{fontWeight:700,color:"var(--cl-danger,#ef4444)",fontSize:14}}>Ineligible Candidates — Age &lt; 1 Year</div>
            <div className="cl-muted" style={{fontSize:12}}>{rows.length} candidate{rows.length!==1?"s":""} cannot appear in any test until they turn 1 year old.</div>
          </div>
        </div>
        <button className="cl-btn cl-btn-ghost cl-btn-sm" onClick={load}>↺ Refresh</button>
      </div>
      <DataTable columns={dtCols} data={rows} customStyles={previewStyles} dense pagination paginationPerPage={10} paginationRowsPerPageOptions={[10,25,50]} persistTableHead conditionalRowStyles={[{when:()=>true,style:{background:"var(--cl-danger-bg,#fef2f2)"}}]}/>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PROFILE FORM
// ─────────────────────────────────────────────────────────────────────────────
function ProfileForm({ form, set, disabled = false, error = "", existingImage = "", imgLoading = false }) {
  const fileInputRef = useRef(null);
  const age = calcAgeInYears(form.dob);
  const ageWarning = form.dob && age !== null && age < 1
    ? `⚠ Age is ${age < 0 ? "invalid" : "less than 1 year"} — this candidate is ineligible for any test.`
    : "";
 
  const meta = idTypeMeta(form.idType);
  const idHint = form.idType === "na"
    ? ""
    : form.idNumber.length === meta.maxLength
      ? `Stored as: ${form.idType === "aadhar" ? `XXXX-${form.idNumber}` : form.idNumber}`
      : "";
 
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => set("image", reader.result);
    reader.readAsDataURL(file);
  };
 
  const previewSrc = form.image || existingImage || defaultImage;
 
  return (
    <div className="profile-section">
      {/* Profile photo — same edit-overlay pattern as MemberForm */}
      <div className="row">
        <div className="col-md-6">
          <div className="mb-3">
            <div className="profile-pic">
              {imgLoading ? (
                <div style={{ width: 120, height: 120, borderRadius: "50%", background: "var(--cl-border,#e5e7eb)" }} />
              ) : (
                <img src={previewSrc} alt="candidate" className="img-fluid" style={{ objectFit: "cover" }} />
              )}
              {!disabled && (
                <>
                  <i className="fas fa-edit" onClick={() => fileInputRef.current?.click()}></i>
                  <input
                    ref={fileInputRef}
                    type="file"
                    style={{ display: "none" }}
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
 
      {/* Name + Gender */}
      <div className="row">
        <div className="col-md-4">
          <div className="mb-3 form-floating">
            <input
              type="text" className="form-control" id="pf_firstName"
              value={form.firstName} onChange={(e) => set("firstName", e.target.value)}
              placeholder="First Name" disabled={disabled}
            />
            <label htmlFor="pf_firstName"><i className="fa fa-user"></i> First Name</label>
          </div>
        </div>
        <div className="col-md-4">
          <div className="mb-3 form-floating">
            <input
              type="text" className="form-control" id="pf_lastName"
              value={form.lastName} onChange={(e) => set("lastName", e.target.value)}
              placeholder="Last Name" disabled={disabled}
            />
            <label htmlFor="pf_lastName"><i className="fa fa-user"></i> Last Name</label>
          </div>
        </div>
        <div className="col-md-4">
          <div className="mb-3 form-floating">
            <select
              id="pf_gender" className="form-select" value={form.gender}
              onChange={(e) => set("gender", e.target.value)} disabled={disabled}
            >
              <option value="">Select Gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
            <label htmlFor="pf_gender">Gender</label>
          </div>
        </div>
      </div>
 
      {/* DOB + Mobile + Email */}
      <div className="row">
        <div className="col-md-4">
          <div className="mb-3 form-floating">
            <input
              type="date" className="form-control" id="pf_dob"
              value={form.dob} onChange={(e) => set("dob", e.target.value)} disabled={disabled}
            />
            <label htmlFor="pf_dob"><i className="fa fa-calendar"></i> Date of Birth</label>
          </div>
          {ageWarning && <p style={{ color: "red", marginTop: -8 }}>{ageWarning}</p>}
        </div>
        <div className="col-md-4">
          <div className="mb-3 form-floating">
            <input
              type="tel" className="form-control" id="pf_mobile"
              value={form.mobile}
              onChange={(e) => set("mobile", e.target.value.replace(/[^0-9]/g, ""))}
              placeholder="Mobile Number" maxLength={13} disabled={disabled}
            />
            <label htmlFor="pf_mobile"><i className="fa fa-phone"></i> Mobile Number</label>
          </div>
        </div>
        <div className="col-md-4">
          <div className="mb-3 form-floating">
            <input
              type="email" className="form-control" id="pf_email"
              value={form.email} onChange={(e) => set("email", e.target.value)}
              placeholder="Email" disabled={disabled}
            />
            <label htmlFor="pf_email"><i className="fa fa-envelope"></i> Email</label>
          </div>
        </div>
      </div>
 
      {/* ID Proof */}
      <div className="row">
        <div className="col-md-4">
          <div className="mb-3 form-floating">
            <select
              id="pf_idType" className="form-select" value={form.idType}
              onChange={(e) => { set("idType", e.target.value); set("idNumber", ""); }}
              disabled={disabled}
            >
              {ID_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <label htmlFor="pf_idType"><i className="bi bi-file-earmark-person form-icon"></i> ID Proof Type</label>
          </div>
        </div>
        {form.idType !== "na" && (
          <div className="col-md-4">
            <div className="mb-3 form-floating">
              <input
                type="text" className="form-control" id="pf_idNumber"
                value={form.idNumber}
                onChange={(e) => set("idNumber", sanitiseIdNumber(form.idType, e.target.value))}
                placeholder={meta.hint} maxLength={meta.maxLength} disabled={disabled}
              />
              <label htmlFor="pf_idNumber">{meta.label.split(" (")[0]} Number</label>
            </div>
            {idHint && <small style={{ color: "green" }}>{idHint}</small>}
          </div>
        )}
      </div>
 
      <hr />
 
      {/* Address — same stacked col-md-6 pattern as MemberForm's Present Address */}
      <div className="row" style={{ top: "20px", position: "relative" }}>
        <h3>Address:</h3>
        <div className="col-md-12" style={{justifyContent:"space-between", display:"flex"}}>
          <div className="mb-3 form-floating">
            <input
              type="text" className="form-control" id="pf_address" placeholder="Address"
              value={form.address} onChange={(e) => set("address", e.target.value)} disabled={disabled}
            />
            <label htmlFor="pf_address"><i className="bi bi-house-fill form-icon"></i> Address</label>
          </div>
          <div className="mb-3 form-floating">
            <input
              type="text" className="form-control" id="pf_pincode" placeholder="Pincode"
              value={form.pincode}
              onChange={(e) => set("pincode", e.target.value.replace(/\D/g, "").slice(0, 6))}
              disabled={disabled}
            />
            <label htmlFor="pf_pincode"><i className="bi bi-mailbox form-icon"></i> Pincode</label>
          </div>
          <div className="mb-3 form-floating">
            <input
              type="text" className="form-control" id="pf_city" placeholder="City"
              value={form.city} onChange={(e) => set("city", e.target.value)} disabled={disabled}
            />
            <label htmlFor="pf_city"><i className="bi bi-building-fill form-icon"></i> City</label>
          </div>
          <div className="mb-3 form-floating">
            <input
              type="text" className="form-control" id="pf_state" placeholder="State"
              value={form.state} onChange={(e) => set("state", e.target.value)} disabled={disabled}
            />
            <label htmlFor="pf_state"><i className="bi bi-geo-alt-fill form-icon"></i> State</label>
          </div>
        </div>
      </div>
 
      {error && <p style={{ color: "red" }}>{error}</p>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TEST ASSIGNMENT SECTION (inside Edit view)
// ─────────────────────────────────────────────────────────────────────────────
function TestAssignSection({ candidate, tests, onSuccess }) {
  const [testId, setTestId] = useState("");
  const [amount, setAmount] = useState("");
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState("");

  const eligible = isTestEligible(candidate);
  const daysLeft = daysUntilEligible(candidate);

  const alreadyAssigned = candidate.hasTest && candidate.testStatus !== "unregistered";
  const canAssign = eligible && !alreadyAssigned;

  const assign = async () => {
    if (!canAssign) {
      if (alreadyAssigned) {
        setError("This candidate already has a test assigned. Unregister them first before assigning a new test.");
      } else {
        setError(`This candidate is not eligible yet — ${daysLeft} day${daysLeft !== 1 ? "s" : ""} remaining until the 1-year gap is complete.`);
      }
      return;
    }
    if (!testId)                  { setError("Please select a test."); return; }
    if (!amount || isNaN(amount)) { setError("Please enter a valid amount."); return; }
    setSaving(true); setError("");
    try {
      await axios.post(
        API.assignTest,
        { candidate_ids: [candidate.id], test_id: testId, amount },
        { headers: authHeaders() }
      );
      onSuccess("✓ Test assigned successfully!");
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Failed to assign test");
    } finally { setSaving(false); }
  };

  const borderColor = !canAssign ? "var(--cl-danger,#ef4444)" : "var(--cl-primary,#6366f1)";
  const icon = !canAssign ? "⛔" : "🎯";

  let lockedMessage = null;
  if (alreadyAssigned) {
    lockedMessage = (
      <span style={{ color: "var(--cl-danger,#ef4444)", fontWeight: 600 }}>
        Already assigned — unregister the candidate first to change their test
      </span>
    );
  } else if (!eligible) {
    lockedMessage = (
      <span style={{ color: "var(--cl-danger,#ef4444)", fontWeight: 600 }}>
        Not eligible — {daysLeft} day{daysLeft !== 1 ? "s" : ""} remaining until 1-year gap is complete
        {candidate.testDate && (
          <span style={{ fontWeight: 400, color: "var(--cl-label)" }}>
            {" "}(last test date: {new Date(candidate.testDate).toLocaleDateString()})
          </span>
        )}
      </span>
    );
  }

  return (
    <Card style={{ border: `1.5px dashed ${borderColor}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <span style={{ fontSize: 22 }}>{icon}</span>
        <div>
          <SectionTitle>Assign Test</SectionTitle>
          <div className="cl-muted" style={{ fontSize: 12, marginTop: -4 }}>
            {lockedMessage ?? (
              candidate.hasTest
                ? "Update test assignment for this candidate."
                : "No test assigned yet. Assign one below."
            )}
          </div>
        </div>
      </div>

      {!canAssign ? (
        <div style={{
          padding: "16px",
          background: "var(--cl-danger-bg,#fef2f2)",
          borderRadius: 8,
          textAlign: "center",
          color: "var(--cl-danger,#ef4444)",
          fontWeight: 600,
          fontSize: 13,
        }}>
          ⛔ Test assignment is locked.<br/>
          <span style={{ fontSize: 12, fontWeight: 400, color: "var(--cl-label)" }}>
            {alreadyAssigned
              ? "Use ↩ Unregister from the candidate list to remove the existing test first."
              : `This candidate must wait ${daysLeft} more day${daysLeft !== 1 ? "s" : ""} before being assigned a new test.`
            }
          </span>
        </div>
      ) : (
        <div className="cl-form-grid" style={{ gridTemplateColumns: "1fr 1fr auto" }}>
          <Field label="Test" required>
            <FSelect value={testId} onChange={setTestId}>
              <option value="">— Select test —</option>
              <TestOptions tests={tests} />
            </FSelect>
          </Field>
          <Field label="Amount (₹)" required>
            <FInput type="number" value={amount} onChange={setAmount} placeholder="0.00"/>
          </Field>
          <Field label=" ">
            <button className="cl-btn cl-btn-primary" onClick={assign} disabled={saving} style={{ marginTop: 2 }}>
              {saving ? "⏳ Assigning…" : "🎯 Assign"}
            </button>
          </Field>
        </div>
      )}

      {error && <InlineError msg={error}/>}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// VIEW: ADD CANDIDATE
// ─────────────────────────────────────────────────────────────────────────────
function AddView({ onSuccess, onBack, batchCtx }) {
  const [form,     setForm]     = useState({ ...EMPTY_PROFILE_FORM });
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState("");
  const [checking, setChecking] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
 
  const validate = () => {
    if (!form.firstName || !form.lastName) return "Full name required";
    return null;
  };
 
  const submit = async () => {
    const err = validate();
    if (err) { setError(err); return; }
 
    setChecking(true); setError("");
    const [nameDobErr] = await Promise.all([
      checkNameDobDuplicate({ firstName: form.firstName, lastName: form.lastName, dob: form.dob }),
    ]);
    setChecking(false);
    if (nameDobErr) { setError(nameDobErr); return; }
 
    setSaving(true); setError("");
    try {
      const fd = new FormData();
      fd.append("first_name",  form.firstName);
      fd.append("last_name",   form.lastName);
      fd.append("email",       form.email);
      fd.append("mobile_no",   form.mobile);
      fd.append("gender",      form.gender);
      fd.append("dob",         form.dob);
      fd.append("address",     form.address);
      fd.append("city",        form.city);
      fd.append("state",       form.state);
      fd.append("pincode",     form.pincode);
      fd.append("id_type",     form.idType);
      fd.append("aadhar_last", form.idType === "na" ? "" : form.idNumber);
      if (form.image) fd.append("candidate_img", form.image);
      if (batchCtx) {
        // institute (batch) candidate — stored as test_candidate.institute = 'yes'
        fd.append("institute_test", "1");
      }
      await axios.post(API.candidates, fd, { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" } });
      onSuccess("✓ Candidate profile created! You can assign a test from the candidate's profile.");
      setForm({ ...EMPTY_PROFILE_FORM });
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Failed to create candidate");
    } finally { setSaving(false); }
  };
 
  const busy = saving || checking;
 
  return (
    <div className="container mt-4">
      <div className="row mb-3 align-items-center">
        <div className="col-md-6">
          <button className="cl-back-btn" onClick={onBack}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M10 3L5 8l5 5"/></svg>
            Back
          </button>
          <h2>Add Candidate</h2>
        </div>
      </div>
 
      <div style={{ padding: "10px 14px", background: "var(--cl-info-bg,#eff6ff)", border: "1px solid var(--cl-info,#3b82f6)", borderRadius: 10, marginBottom: 16, display: "flex", gap: 10, alignItems: "flex-start" }}>
        <span style={{ fontSize: 18 }}>ℹ️</span>
        <div style={{ fontSize: 13, color: "var(--cl-info-text,#1d4ed8)" }}>
          <strong>Profile-first flow:</strong> Create the candidate profile here. You can assign a test later from the candidate's edit page, or use <strong>Assign Test</strong> from the list to assign tests to multiple candidates at once.
        </div>
      </div>
 
      <ProfileForm form={form} set={set} error={error} />
 
      <div className="row" style={{ paddingTop: "40px", position: "relative", display: "flex" }}>
        <div className="col-md-6 col-sm-6" style={{ textAlign: "right" }}>
          <button className="btn btn-danger" disabled={busy} onClick={() => { setForm({ ...EMPTY_PROFILE_FORM }); setError(""); }}>
            Reset
          </button>
        </div>
        <div className="col-md-6 col-sm-6">
          <button type="submit" style={{ borderRadius: "8px" }} disabled={busy} onClick={submit}>
            {checking ? "Checking duplicates…" : saving ? "Saving…" : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
}
 

// ─────────────────────────────────────────────────────────────────────────────
// VIEW: EDIT CANDIDATE
// ─────────────────────────────────────────────────────────────────────────────
function EditView({ candidate, tests, onSuccess, onBack, onDeactivate, onDelete, onUnregister}) {
  const [form,         setForm]         = useState({ ...EMPTY_PROFILE_FORM });
  const [candidateImg, setCandidateImg] = useState("");
  const [imgLoading,   setImgLoading]   = useState(false);
  const [editing,      setEditing]      = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [checking,     setChecking]     = useState(false);
  const [error,        setError]        = useState("");
  const roleid = getRoleId();
 
  useEffect(() => {
    if (!candidate) return;
    const formattedDOB = candidate.dob ? new Date(candidate.dob).toLocaleDateString('en-CA') : "";
    setForm({
      firstName: candidate.firstName || "",
      lastName:  candidate.lastName  || "",
      email:     candidate.email     || "",
      mobile:    candidate.mobile    || "",
      gender:    candidate.gender    || "",
      dob:       formattedDOB,
      address:   candidate.address   || "",
      city:      candidate.city      || "",
      state:     candidate.state     || "",
      pincode:   candidate.pincode   || "",
      idType:    candidate.idType    || "aadhar",
      idNumber:  candidate.idNumber  || "",
      image:     null,
    });
    setEditing(false); setError(""); setCandidateImg("");
    setImgLoading(true);
    axios.get(API.candidate(candidate.id), { headers: authHeaders() })
      .then((r) => setCandidateImg((r.data.candidate_img || "").replace(/[\r\n\s]/g, "")))
      .catch(() => {})
      .finally(() => setImgLoading(false));
  }, [candidate]);
 
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
 
  const validate = () => {
    if (!form.firstName || !form.lastName) return "Full name required";
    return null;
  };
 
  const save = async () => {
    const err = validate();
    if (err) { setError(err); return; }
 
    setChecking(true); setError("");
    const [nameDobErr] = await Promise.all([
      checkNameDobDuplicate({ firstName: form.firstName, lastName: form.lastName, dob: form.dob, excludeId: candidate.id }),
    ]);
    setChecking(false);
    if (nameDobErr) { setError(nameDobErr); return; }
 
    setSaving(true); setError("");
    try {
      const fd = new FormData();
      fd.append("first_name",  form.firstName);
      fd.append("last_name",   form.lastName);
      fd.append("email",       form.email);
      fd.append("mobile_no",   form.mobile);
      fd.append("gender",      form.gender);
      fd.append("dob",         form.dob);
      fd.append("address",     form.address || "");
      fd.append("city",        form.city    || "");
      fd.append("state",       form.state   || "");
      fd.append("pincode",     form.pincode || "");
      fd.append("id_type",     form.idType);
      fd.append("aadhar_last", form.idType === "na" ? "" : form.idNumber);
      if (form.image) fd.append("candidate_img", form.image);
      await axios.put(API.candidate(candidate.id), fd, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      });
      onSuccess("✓ Candidate profile updated successfully!");
      setEditing(false);
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Failed to update");
    } finally { setSaving(false); }
  };
 
  if (!candidate) return null;
  const isDeactivated = candidate.status === "deactivated";
  const busy = saving || checking;
 
  return (
    <div className="container mt-4">
      <div className="row mb-3 align-items-center">
        <div className="col-md-6">
          <button className="cl-back-btn" onClick={onBack}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M10 3L5 8l5 5"/></svg>
            Back
          </button>
          <h2>
            {form.firstName} {form.lastName} <StatusBadge status={candidate.status} />
            {candidate.hasTest
              ? <span className="cl-badge badge-ok" style={{ marginLeft: 6, fontSize: 11 }}>🎯 {candidate.test}</span>
              : <span className="cl-badge badge-o" style={{ marginLeft: 6, fontSize: 11 }}>No test assigned</span>}
          </h2>
        </div>
        {!editing && (
          <div className="col-md-6" style={{ textAlign: "right" }}>
            {roleid === 1 && candidate.hasTest && (
              <button
                className="btn btn-outline-secondary"
                style={{ marginRight: 8 }}
                onClick={() => onUnregister(candidate)}
                title="Unregister from test"
              >
                ↩ Unregister
              </button>
            )}

            {roleid === 1 && <button className="btn btn-danger" style={{ marginRight: 8 }} onClick={() => onDelete(candidate)}>Delete</button>}
            {!isDeactivated && <button className="btn btn-warning" style={{ marginRight: 8 }} onClick={() => onDeactivate(candidate)}>Deactivate</button>}
            <button className="btn btn-primary" onClick={() => setEditing(true)}>Edit</button>
          </div>
        )}
      </div>
 
      <ProfileForm
        form={form} set={set} disabled={!editing} error={error}
        existingImage={candidateImg} imgLoading={imgLoading}
      />
 
      {editing && (
        <div className="row" style={{ paddingTop: "20px", position: "relative", display: "flex" }}>
          <div className="col-md-6 col-sm-6" style={{ textAlign: "right" }}>
            <button className="btn btn-danger" disabled={busy} onClick={() => { setEditing(false); setError(""); }}>Cancel</button>
          </div>
          <div className="col-md-6 col-sm-6">
            <button type="submit" style={{ borderRadius: "8px" }} disabled={busy} onClick={save}>
              {checking ? "Checking duplicates…" : saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </div>
      )}
 
      {!editing && (
        <div style={{ marginTop: 24 }}>
          <TestAssignSection candidate={candidate} tests={tests} onSuccess={(msg) => onSuccess(msg)} />
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// VIEW: BULK UPLOAD — Step 1: upload profiles, Step 2: assign test
// ─────────────────────────────────────────────────────────────────────────────
function BulkView({ tests, onSuccess, onBack, batchCtx }) {
  const [step,      setStep]     = useState(1);

  const [file,      setFile]      = useState(null);
  const [rows,      setRows]      = useState([]);
  const [rowImages, setRowImages] = useState({});
  const [parsing,   setParsing]   = useState(false);
  const [progress,  setProgress]  = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error,     setError]     = useState("");
  const [drag,      setDrag]      = useState(false);

  const [idDupRows,      setIdDupRows]      = useState(new Set());
  const [nameDobDupRows, setNameDobDupRows] = useState(new Set());
  const [batchDupRows,   setBatchDupRows]   = useState(new Set());

  const [uploadedCandidates, setUploadedCandidates] = useState([]);
  const [selectedIds,        setSelectedIds]         = useState(new Set());
  const [testId,             setTestId]              = useState("");
  const [amount,             setAmount]              = useState("");
  const [assigning,          setAssigning]           = useState(false);
  const [assignError,        setAssignError]         = useState("");

  const fileRef = useRef();

  const handleFile = useCallback((f) => {
    if (!f) return;
    const name = f.name.toLowerCase();
    if (!name.endsWith(".xlsx") && !name.endsWith(".xls")) { setError("Only .xlsx files allowed"); return; }
    if (f.size > 10 * 1024 * 1024) { setError("Max file size is 10 MB"); return; }
    setError("");
    setFile(f); setRows([]); setRowImages({}); setProgress(0); setParsing(true);
    setIdDupRows(new Set()); setNameDobDupRows(new Set()); setBatchDupRows(new Set());
    const reader = new FileReader();
    reader.onload = (e) => {
      setProgress(40);
      setTimeout(() => {
        try   { setProgress(100); setRows(parseExcel(e.target.result)); }
        catch (err) { setError("Parse failed: " + err.message); }
        finally { setParsing(false); }
      }, 300);
    };
    reader.readAsArrayBuffer(f);
  }, []);

  const clearFile = () => {
    setFile(null); setRows([]); setRowImages({});
    setProgress(0); setParsing(false);
    setIdDupRows(new Set()); setNameDobDupRows(new Set()); setBatchDupRows(new Set());
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleRowImage  = useCallback(async (rowIdx, imageFile) => {
    if (!imageFile) return;
    try { const c = await compressImage(imageFile); setRowImages((p) => ({ ...p, [rowIdx]: c })); }
    catch (e) { console.warn("Compress failed", e); }
  }, []);
  const clearRowImage   = useCallback((rowIdx) => setRowImages((p) => { const n={...p}; delete n[rowIdx]; return n; }), []);
  const clearAllImages  = () => setRowImages({});

  const validRows   = rows.filter((r) => !r._error);
  const invalidRows = rows.filter((r) =>  r._error);
  const ageRows     = invalidRows.filter((r) => r._error === "Age < 1 yr — ineligible");
  const withImages  = validRows.filter((r) => rowImages[r._rowIdx]);
  const allDupRows  = new Set([...idDupRows, ...nameDobDupRows, ...batchDupRows]);

  // ── Step 1: upload profiles ────────────────────────────────────────────────
  const submitProfiles = async () => {
    if (!validRows.length) { setError("No valid rows to upload"); return; }
    setUploading(true); setError("");
    setIdDupRows(new Set()); setNameDobDupRows(new Set()); setBatchDupRows(new Set());

    // In-file duplicate detection: name+dob always; id_type+aadhar_last only
    // when an actual ID was provided (skip "na"/blank).
    const batchDupSet = new Set();
    const seenNameDob = new Map();
    const seenId      = new Map();
    for (const r of validRows) {
      const nameDobKey = `${r.first_name.trim().toLowerCase()}|${r.last_name.trim().toLowerCase()}|${r.dob}`;
      if (seenNameDob.has(nameDobKey)) { batchDupSet.add(r._rowIdx); batchDupSet.add(seenNameDob.get(nameDobKey)); }
      else seenNameDob.set(nameDobKey, r._rowIdx);

      if (r.id_type !== "na" && r.aadhar_last) {
        const idKey = `${r.id_type}|${r.aadhar_last}`;
        if (seenId.has(idKey)) { batchDupSet.add(r._rowIdx); batchDupSet.add(seenId.get(idKey)); }
        else seenId.set(idKey, r._rowIdx);
      }
    }
    if (batchDupSet.size > 0) {
      setBatchDupRows(batchDupSet);
      const names = validRows.filter((r)=>batchDupSet.has(r._rowIdx)).map((r)=>`${r.first_name} ${r.last_name}`).join(", ");
      setError(`Duplicate rows found within the uploaded file: ${names}. Please fix and re-upload.`);
      setUploading(false); return;
    }

    const [idResults, nameDobResults] = await Promise.all([
      Promise.all(validRows.map(async (r) => {
        // const err = await checkIdDuplicate({ idType: r.id_type, idNumber: r.aadhar_last });
        return { rowIdx: r._rowIdx, name: `${r.first_name} ${r.last_name}`};
      })),
      Promise.all(validRows.map(async (r) => {
        const err = await checkNameDobDuplicate({ firstName: r.first_name, lastName: r.last_name, dob: r.dob });
        return { rowIdx: r._rowIdx, name: `${r.first_name} ${r.last_name}`, err };
      })),
    ]);

    const idDupSet      = new Set(idResults     .filter((x)=>x.err).map((x)=>x.rowIdx));
    const nameDobDupSet = new Set(nameDobResults.filter((x)=>x.err).map((x)=>x.rowIdx));
    if (idDupSet.size > 0 || nameDobDupSet.size > 0) {
      if (idDupSet.size      > 0) setIdDupRows(idDupSet);
      if (nameDobDupSet.size > 0) setNameDobDupRows(nameDobDupSet);
      const parts = [];
      const nameDobNames = nameDobResults.filter((x)=>x.err).map((x)=>x.name);
      const idNames      = idResults     .filter((x)=>x.err).map((x)=>x.name);
      if (nameDobNames.length) parts.push(`Same name+DOB already in DB: ${nameDobNames.join(", ")}`);
      if (idNames.length)      parts.push(`Duplicate ID proof in DB: ${idNames.join(", ")}`);
      setError(parts.join("  |  ") + "  — Please correct and re-upload.");
      setUploading(false); return;
    }

    try {
      const payload = validRows.map(({ _error, _rowIdx, ...r }) => ({
        ...r,
        gender:        (r.gender || "").toLowerCase(),
        candidate_img: rowImages[_rowIdx] || r.candidate_img || null,
      }));
      const res = await axios.post(
        API.bulk,
        {
          candidates: payload,
          ...(batchCtx && { institute_test: 1 }),
        },
        { headers: authHeaders() }
      );
      const created = res.data.candidates || res.data.data || [];
      const normalised = created.map(normaliseRow);
      setUploadedCandidates(normalised);
      setSelectedIds(new Set(
        normalised
          .filter((c) => isTestEligible(c))
          .map((c) => c.id)
      ));
      setStep(2);
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  // ── Step 2: assign test ────────────────────────────────────────────────────
  const submitAssign = async () => {
    if (!testId)                  { setAssignError("Please select a test."); return; }
    if (!amount || isNaN(amount)) { setAssignError("Please enter a valid amount."); return; }

    const eligibleSelected = [...selectedIds].filter((id) => {
      const c = uploadedCandidates.find((x) => x.id === id);
      return c && isTestEligible(c);
    });

    if (!eligibleSelected.length) { setAssignError("No eligible candidates selected for assignment."); return; }

    setAssigning(true); setAssignError("");
    try {
      await axios.post(
        API.bulkAssignTest,
        { candidate_ids: eligibleSelected, test_id: testId, amount },
        { headers: authHeaders() }
      );
      onSuccess(`✓ Profiles created & test assigned to ${eligibleSelected.length} candidate${eligibleSelected.length !== 1 ? "s" : ""}!`);
      clearFile();
      setStep(1);
      setUploadedCandidates([]);
      setSelectedIds(new Set());
      setTestId(""); setAmount("");
    } catch (e) {
      setAssignError(e.response?.data?.error || e.message || "Assignment failed");
    } finally { setAssigning(false); }
  };

  const skipAssign = () => {
    onSuccess(`✓ ${uploadedCandidates.length} candidate profile${uploadedCandidates.length !== 1 ? "s" : ""} created! You can assign tests later.`);
    clearFile();
    setStep(1);
    setUploadedCandidates([]);
    setSelectedIds(new Set());
  };

  const toggleSelect = (id, eligible) => {
    if (!eligible) return;
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const selectAll   = () => setSelectedIds(new Set(
    uploadedCandidates.filter((c) => isTestEligible(c)).map((c) => c.id)
  ));
  const deselectAll = () => setSelectedIds(new Set());

  const previewStyles = {
    table:     { style:{ background:"transparent", fontSize:"12px" } },
    headRow:   { style:{ background:"var(--cl-th-bg)", borderBottomColor:"var(--cl-border)", minHeight:"36px" } },
    headCells: { style:{ fontSize:"10px", fontWeight:700, color:"var(--cl-label)", letterSpacing:"0.06em", padding:"0 10px" } },
    rows:      { style:{ fontSize:"12px", borderBottomColor:"var(--cl-border)", minHeight:"44px" } },
    cells:     { style:{ padding:"0 10px" } },
    pagination:{ style:{ fontSize:"12px", color:"var(--cl-label)", borderTopColor:"var(--cl-border)" } },
  };

  const previewCols = [
    { name:"#", width:"48px", cell:(_,i) => <span className="cl-muted">{i+1}</span> },
    { name:"First",  selector:(r) => r.first_name, sortable:true },
    { name:"Last",   selector:(r) => r.last_name,  sortable:true },
    { name:"Email",  selector:(r) => r.email, sortable:true, grow:2, cell:(r) => <span className="cl-muted">{r.email}</span> },
    { name:"Mobile", selector:(r) => r.mobile, width:"120px" },
    { name:"Gender", selector:(r) => r.gender, width:"90px", cell:(r) => <GenderBadge gender={r.gender}/> },
    { name:"DOB",    selector:(r) => r.dob, width:"110px" },
    { name:"City",   selector:(r) => r.city },
    {
      name:"ID Proof", width:"160px",
      cell:(r) => {
        if (r._error) return null;
        const isIdDup    = idDupRows.has(r._rowIdx);
        const isBatchDup = batchDupRows.has(r._rowIdx);
        const isDup      = isIdDup || isBatchDup;
        return (
          <div>
            <span style={{fontFamily:"monospace",fontSize:12,color:isDup?"var(--cl-danger,#ef4444)":"inherit",fontWeight:isDup?600:400}}>
              {idProofDisplay(r.id_type, r.aadhar_last)}
            </span>
            {isIdDup    && <div style={{fontSize:10,color:"var(--cl-danger,#ef4444)",fontWeight:600}}>⚠ DB duplicate</div>}
            {isBatchDup && <div style={{fontSize:10,color:"var(--cl-danger,#ef4444)",fontWeight:600}}>⚠ In-file duplicate</div>}
          </div>
        );
      },
    },
    {
      name:"Photo", width:"100px",
      cell:(r) => {
        const img = rowImages[r._rowIdx];
        const isMissing = !r._error && !img;
        return img ? (
          <div style={{display:"flex",alignItems:"center",gap:4}}>
            <img src={img} alt="" width="34" height="34" style={{borderRadius:"50%",objectFit:"cover",border:"2px solid var(--cl-border)"}}/>
            <button title="Remove photo" onClick={() => clearRowImage(r._rowIdx)} style={{background:"none",border:"none",cursor:"pointer",color:"var(--cl-danger,#ef4444)",fontSize:14,lineHeight:1,padding:2}}>✕</button>
          </div>
        ) : (
          <label style={{display:"flex",alignItems:"center",gap:4,cursor:"pointer",fontSize:11,color:"var(--cl-primary,#6366f1)",userSelect:"none"}}>
            <span style={{fontSize:18,lineHeight:1}}>📷</span>
            <span>Add</span>
            {isMissing && <span style={{fontSize:15,color:"var(--cl-danger,#ef4444)",fontWeight:600}}>*</span>}
            <input type="file" accept="image/*" style={{display:"none"}} onChange={(e) => handleRowImage(r._rowIdx, e.target.files[0])}/>
          </label>
        );
      },
    },
    {
      name:"Status", width:"170px",
      cell:(r) => {
        if (batchDupRows.has(r._rowIdx))   return <span className="cl-badge badge-err" title="Duplicate within this file">⚠ In-file dup</span>;
        if (nameDobDupRows.has(r._rowIdx)) return <span className="cl-badge badge-err" title="Same name+DOB already in DB">⚠ Name+DOB dup</span>;
        if (idDupRows.has(r._rowIdx))      return <span className="cl-badge badge-err" title="ID proof already in DB">⚠ ID dup</span>;
        if (r._error)                      return <span className="cl-badge badge-err" title={r._error}>⚠ {r._error}</span>;
        return <span className="cl-badge badge-ok">✓ Valid</span>;
      },
    },
  ];

  const StepBar = () => (
    <div style={{ display:"flex", alignItems:"center", gap:0, marginBottom:20 }}>
      {[
        { n:1, label:"Upload Profiles" },
        { n:2, label:"Assign Test" },
      ].map((s, i, arr) => (
        <div key={`step-wrap-${s.n}`} style={{ display:"flex", alignItems:"center" }}>
          <div style={{
            display:"flex", alignItems:"center", gap:8,
            padding:"8px 16px", borderRadius:8,
            background: step === s.n ? "var(--cl-primary,#6366f1)" : step > s.n ? "var(--cl-success,#10b981)" : "var(--cl-th-bg,#f3f4f6)",
            color: step >= s.n ? "#fff" : "var(--cl-label,#888)",
            fontWeight:600, fontSize:13, transition:"all 0.2s",
          }}>
            <span style={{
              width:22, height:22, borderRadius:"50%",
              background: step > s.n ? "rgba(255,255,255,0.3)" : "rgba(255,255,255,0.2)",
              display:"inline-flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:700,
            }}>
              {step > s.n ? "✓" : s.n}
            </span>
            {s.label}
          </div>
          {i < arr.length - 1 && (
            <div style={{
              height:2, width:32,
              background: step > 1 ? "var(--cl-success,#10b981)" : "var(--cl-border,#e5e7eb)",
              transition:"all 0.3s",
            }}/>
          )}
        </div>
      ))}
    </div>
  );

  const eligibleUploaded   = uploadedCandidates.filter((c) =>  isTestEligible(c));
  const ineligibleUploaded = uploadedCandidates.filter((c) => !isTestEligible(c));

  return (
    <div className="cl-view">
      <div className="profile-section">
        <div className="cl-view-header">
          <button className="cl-back-btn" onClick={() => { if (step === 2) setStep(1); else onBack(); }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M10 3L5 8l5 5"/></svg>
            {step === 2 ? "Back to Upload" : "Back"}
          </button>
          <h2 className="cl-view-title">Bulk Upload</h2>
        </div>

        <StepBar/>

        {step === 1 && (
          <>
            <Card>
              <div className="cl-step-row"><div className="cl-step-num">1</div><SectionTitle>Download Sample Template</SectionTitle></div>
              <div className="cl-sample-bar">
                <div className="cl-sample-info">
                  <span className="cl-sample-icon">📄</span>
                  <div>
                    <div className="cl-sample-name">candidate_profile_sample (.xlsx)</div>
                    <div className="cl-sample-hint">
                      Fill in personal details only — <strong>no test or amount columns</strong>. You'll assign tests in the next step.
                      Set <strong>id_type</strong> to one of aadhar / pan / dl / passport / na, and fill <strong>aadhar_last</strong> accordingly
                      (leave blank if id_type is <strong>na</strong>).
                    </div>
                  </div>
                </div>
                <button className="cl-btn cl-btn-ghost cl-btn-sm" onClick={downloadSampleExcel}>⬇ Excel</button>
              </div>
            </Card>

            <Card>
              <div className="cl-step-row"><div className="cl-step-num">2</div><SectionTitle>Upload Excel</SectionTitle></div>
              <div
                className={`cl-drop-zone${drag ? " cl-drop-active" : ""}`}
                onClick={() => fileRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                onDragLeave={() => setDrag(false)}
                onDrop={(e) => { e.preventDefault(); setDrag(false); handleFile(e.dataTransfer.files[0]); }}
              >
                <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{display:"none"}} onChange={(e) => handleFile(e.target.files[0])}/>
                <div className="cl-drop-icon">📂</div>
                <div className="cl-drop-title">Drag &amp; drop your file here</div>
                <div className="cl-drop-sub">or <span>click to browse</span></div>
                <div className="cl-drop-note">.xlsx · max 10 MB</div>
              </div>

              {file && (
                <div className="cl-file-chip">
                  <span>📊</span>
                  <div className="cl-file-chip-info">
                    <span className="cl-file-chip-name">{file.name}</span>
                    <span className="cl-file-chip-size">{(file.size / 1024).toFixed(1)} KB · Excel</span>
                  </div>
                  <button className="cl-file-chip-rm" onClick={clearFile}>✕</button>
                </div>
              )}

              {parsing && (
                <div className="cl-prog-wrap">
                  <div className="cl-prog-bg"><div className="cl-prog-bar" style={{width:`${progress}%`}}/></div>
                  <span className="cl-prog-label">{progress < 50 ? "Reading file…" : "Parsing rows…"}</span>
                </div>
              )}
            </Card>

            {ageRows.length > 0 && rows.length > 0 && (
              <div style={{background:"var(--cl-danger-bg,#fef2f2)",border:"1px solid var(--cl-danger,#ef4444)",borderRadius:10,padding:"10px 16px",display:"flex",alignItems:"flex-start",gap:10}}>
                <span style={{fontSize:20}}>⚠️</span>
                <div>
                  <div style={{fontWeight:700,color:"var(--cl-danger,#ef4444)",fontSize:13}}>{ageRows.length} row{ageRows.length!==1?"s":""} skipped — age &lt; 1 year</div>
                  <div className="cl-muted" style={{fontSize:12}}>These rows will <strong>not</strong> be uploaded. Names: {ageRows.map((r)=>`${r.first_name} ${r.last_name}`).join(", ")}</div>
                </div>
              </div>
            )}

            {rows.length > 0 && (
              <Card>
                <div className="cl-preview-header">
                  <div className="cl-step-row" style={{marginBottom:0}}>
                    <div className="cl-step-num">3</div>
                    <SectionTitle>Preview, Add Photos &amp; Create Profiles</SectionTitle>
                  </div>
                  <div className="cl-preview-stats">
                    <span>{rows.length} rows</span>
                    <span className="cl-stat-ok">· {validRows.length} valid</span>
                    {invalidRows.length > 0  && <span className="cl-stat-err">· {invalidRows.length} errors</span>}
                    {ageRows.length > 0      && <span className="cl-stat-err">· {ageRows.length} ineligible</span>}
                    {batchDupRows.size > 0   && <span className="cl-stat-err">· {batchDupRows.size} in-file dup</span>}
                    {nameDobDupRows.size > 0 && <span className="cl-stat-err">· {nameDobDupRows.size} name+DOB dup</span>}
                    {idDupRows.size > 0      && <span className="cl-stat-err">· {idDupRows.size} ID dup</span>}
                    {withImages.length > 0 && (
                      <span className="cl-stat-ok" style={{display:"flex",alignItems:"center",gap:4}}>
                        · 📷 {withImages.length} with photo
                        <button onClick={clearAllImages} style={{background:"none",border:"none",cursor:"pointer",color:"var(--cl-label)",fontSize:11,padding:"0 2px"}}>(clear all)</button>
                      </span>
                    )}
                  </div>
                </div>

                <div style={{padding:"8px 12px",background:"var(--cl-th-bg,#f8f9fa)",borderRadius:8,margin:"8px 0 4px",fontSize:12,color:"var(--cl-label,#666)"}}>
                  📷 Click <strong>Add</strong> in the Photo column to pick an image.&nbsp;|&nbsp;
                  🎯 After profiles are created you'll choose a test to assign in Step 2.
                </div>

                <div className="cl-preview-table-wrap">
                  <DataTable
                    columns={previewCols} data={rows} customStyles={previewStyles} dense
                    pagination paginationPerPage={10} paginationRowsPerPageOptions={[10,25,50]} persistTableHead
                    conditionalRowStyles={[
                      { when:(r) => batchDupRows.has(r._rowIdx),                                                                    style:{ background:"var(--cl-danger-bg,#fef2f2)" } },
                      { when:(r) => nameDobDupRows.has(r._rowIdx) && !batchDupRows.has(r._rowIdx),                                  style:{ background:"var(--cl-danger-bg,#fef2f2)" } },
                      { when:(r) => idDupRows.has(r._rowIdx) && !batchDupRows.has(r._rowIdx) && !nameDobDupRows.has(r._rowIdx),     style:{ background:"var(--cl-danger-bg,#fef2f2)" } },
                      { when:(r) => r._error === "Age < 1 yr — ineligible",                                                         style:{ background:"var(--cl-danger-bg,#fef2f2)" } },
                      { when:(r) => !!r._error && r._error !== "Age < 1 yr — ineligible",                                           style:{ background:"#fff7ed" } },
                      { when:(r) => !r._error && !!rowImages[r._rowIdx] && !allDupRows.has(r._rowIdx),                              style:{ background:"var(--cl-success-bg,#f0fdf4)" } },
                    ]}
                  />
                </div>

                {error && <InlineError msg={error}/>}

                <div className="cl-form-actions" style={{marginTop:16}}>
                  <button className="cl-btn cl-btn-ghost" onClick={clearFile} disabled={uploading}>✕ Clear</button>
                  <button
                    className="cl-btn cl-btn-primary"
                    onClick={submitProfiles}
                    disabled={uploading || !validRows.length}
                  >
                    {uploading
                      ? "⏳ Checking & uploading…"
                      : withImages.length < validRows.length
                        ? `⬆ Create ${validRows.length} Profiles — ⚠ ${validRows.length - withImages.length} photo${validRows.length - withImages.length !== 1 ? "s" : ""} missing`
                        : `⬆ Create ${validRows.length} Profiles → Assign Test`}
                  </button>
                </div>
              </Card>
            )}

            {!rows.length && error && <InlineError msg={error}/>}
          </>
        )}

        {step === 2 && (
          <>
            <div style={{padding:"12px 16px",background:"var(--cl-success-bg,#f0fdf4)",border:"1px solid var(--cl-success,#10b981)",borderRadius:10,marginBottom:16,display:"flex",gap:10,alignItems:"center"}}>
              <span style={{fontSize:22}}>🎉</span>
              <div>
                <div style={{fontWeight:700,color:"var(--cl-success,#10b981)",fontSize:14}}>
                  {uploadedCandidates.length} candidate profile{uploadedCandidates.length !== 1 ? "s" : ""} created successfully!
                </div>
                <div className="cl-muted" style={{fontSize:12}}>
                  {eligibleUploaded.length} eligible for test assignment
                  {ineligibleUploaded.length > 0 && ` · ${ineligibleUploaded.length} locked (1-year gap not met)`}.
                  Select candidates below or skip to assign later.
                </div>
              </div>
            </div>

            {ineligibleUploaded.length > 0 && (
              <div style={{
                marginBottom:14, padding:"10px 14px",
                background:"var(--cl-danger-bg,#fef2f2)",
                border:"1px solid var(--cl-danger,#ef4444)",
                borderRadius:10, display:"flex", gap:10, alignItems:"flex-start",
              }}>
                <span style={{fontSize:20}}>⛔</span>
                <div>
                  <div style={{fontWeight:700,color:"var(--cl-danger,#ef4444)",fontSize:13}}>
                    {ineligibleUploaded.length} candidate{ineligibleUploaded.length !== 1 ? "s" : ""} cannot be assigned a test yet
                  </div>
                  <div className="cl-muted" style={{fontSize:12}}>
                    These candidates have an existing test date within the last year. They are shown with a lock icon below and cannot be selected.
                  </div>
                </div>
              </div>
            )}

            <Card>
              <SectionTitle>🎯 Assign Test (Optional)</SectionTitle>
              <div className="cl-form-grid" style={{ gridTemplateColumns:"1fr 1fr" }}>
                <Field label="Test" required>
                  <FSelect value={testId} onChange={(v) => { setTestId(v); setAssignError(""); }}>
                    <option value="">— Select test —</option>
                    <TestOptions tests={tests} />
                  </FSelect>
                  {assignError && !testId && (
                    <div style={{marginTop:4,fontSize:12,color:"var(--cl-danger,#ef4444)",fontWeight:500}}>
                      ⚠ Please select a test
                    </div>
                  )}
                </Field>
                <Field label="Amount per candidate (₹)" required>
                  <FInput
                    type="number"
                    value={amount}
                    onChange={(v) => { setAmount(v); setAssignError(""); }}
                    placeholder="0.00"
                  />
                  {assignError && testId && (!amount || isNaN(amount)) && (
                    <div style={{marginTop:4,fontSize:12,color:"var(--cl-danger,#ef4444)",fontWeight:500}}>
                      ⚠ Please enter a valid amount
                    </div>
                  )}
                </Field>
              </div>
            </Card>

            <Card>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
                <SectionTitle>
                  Select Candidates ({selectedIds.size} / {eligibleUploaded.length} eligible selected)
                </SectionTitle>
                <div style={{ display:"flex", gap:8 }}>
                  <button className="cl-btn cl-btn-ghost cl-btn-sm" onClick={selectAll}>Select All Eligible</button>
                  <button className="cl-btn cl-btn-ghost cl-btn-sm" onClick={deselectAll}>Deselect All</button>
                </div>
              </div>

              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))", gap:10 }}>
                {uploadedCandidates.map((c) => {
                  const eligible   = isTestEligible(c);
                  const daysLeft   = daysUntilEligible(c);
                  const isSelected = selectedIds.has(c.id);

                  return (
                    <div
                      key={c.id}
                      onClick={() => toggleSelect(c.id, eligible)}
                      style={{
                        display:"flex", alignItems:"center", gap:10, padding:"10px 12px",
                        border:`2px solid ${
                          !eligible        ? "var(--cl-danger,#ef4444)"  :
                          isSelected       ? "var(--cl-primary,#6366f1)" :
                                             "var(--cl-border,#e5e7eb)"
                        }`,
                        borderRadius:10,
                        cursor: eligible ? "pointer" : "not-allowed",
                        transition:"all 0.15s",
                        background:
                          !eligible  ? "var(--cl-danger-bg,#fef2f2)"  :
                          isSelected ? "var(--cl-primary-bg,#eef2ff)" : "transparent",
                        opacity: eligible ? 1 : 0.75,
                      }}
                    >
                      <div style={{
                        width:20, height:20, borderRadius:4, flexShrink:0,
                        border:`2px solid ${
                          !eligible  ? "var(--cl-danger,#ef4444)"  :
                          isSelected ? "var(--cl-primary,#6366f1)" :
                                       "var(--cl-border,#e5e7eb)"
                        }`,
                        background:
                          !eligible  ? "var(--cl-danger,#ef4444)"  :
                          isSelected ? "var(--cl-primary,#6366f1)" : "transparent",
                        display:"flex", alignItems:"center", justifyContent:"center",
                        color:"#fff", fontSize:11, fontWeight:700, transition:"all 0.15s",
                      }}>
                        {!eligible ? "🔒" : isSelected ? "✓" : ""}
                      </div>

                      <div style={{ minWidth:0 }}>
                        <div style={{ fontWeight:600, fontSize:13 }}>{c.firstName} {c.lastName}</div>
                        <div className="cl-muted" style={{ fontSize:11, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{c.email}</div>
                        {!eligible
                          ? <span className="cl-badge badge-err" style={{ fontSize:10 }}>
                              ⛔ {daysLeft}d until eligible
                            </span>
                          : <GenderBadge gender={c.gender}/>
                        }
                      </div>
                    </div>
                  );
                })}
              </div>

              {assignError && (
                <div style={{marginTop:12}}>
                  <InlineError msg={assignError}/>
                </div>
              )}
            </Card>

            <div className="cl-form-actions">
              <button className="cl-btn cl-btn-ghost" onClick={skipAssign} disabled={assigning}>
                Skip — Assign Later
              </button>
              <button
                className="cl-btn cl-btn-primary"
                onClick={submitAssign}
                disabled={assigning || !selectedIds.size}
              >
                {assigning
                  ? "⏳ Assigning…"
                  : `🎯 Assign Test to ${selectedIds.size} Candidate${selectedIds.size !== 1 ? "s" : ""}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// VIEW: LIST  (split into two tables)
// ─────────────────────────────────────────────────────────────────────────────
function ListView({ statsLoading, stats, genderFilter, setGenderFilter, statusFilter, setStatusFilter, refreshKey, tests, onAdd, onBulk, onEdit, onUnregister, onDeactivate, onDelete, onAssignSuccess }) {
  const tableRef1  = useRef(null);
  const tableRef2  = useRef(null);
  const dtRef1     = useRef(null);
  const dtRef2     = useRef(null);
  const mounted1   = useRef(false);
  const mounted2   = useRef(false);
  const roleid     = getRoleId();
  const selectedRowsRef        = useRef([]);
  const selectedPassedRowsRef  = useRef([]);
  const allPendingRowsRef      = useRef([]);
  const allPassedRowsRef       = useRef([]);

  const [activeTab,            setActiveTab]            = useState("pending");
  const [selectedRows,         setSelectedRows]         = useState([]);
  const [selectedPassedRows,   setSelectedPassedRows]   = useState([]);
  const [showAssignModal,      setShowAssignModal]      = useState(false);
  const [showMailConfirm,      setShowMailConfirm]      = useState(false);
  const [mailSending,          setMailSending]          = useState(false);
  const [showPriorEvidence, setShowPriorEvidence] = useState(false);
  const genderRef       = useRef(genderFilter);
  const statusRef       = useRef(statusFilter);
  const onEditRef       = useRef(onEdit);
  const onUnregisterRef = useRef(onUnregister);
  const onDeactivateRef = useRef(onDeactivate);
  const onDeleteRef     = useRef(onDelete);

  const isFirstReloadRun = useRef(true);

  useEffect(() => { genderRef.current       = genderFilter; }, [genderFilter]);
  useEffect(() => { statusRef.current       = statusFilter; }, [statusFilter]);
  useEffect(() => { onEditRef.current       = onEdit;       }, [onEdit]);
  useEffect(() => { onUnregisterRef.current = onUnregister; }, [onUnregister]);
  useEffect(() => { onDeactivateRef.current = onDeactivate; }, [onDeactivate]);
  useEffect(() => { onDeleteRef.current     = onDelete;     }, [onDelete]);
  useEffect(() => { selectedRowsRef.current = selectedRows; }, [selectedRows]);

  useEffect(() => {
    if (isFirstReloadRun.current) {
      isFirstReloadRun.current = false;
      return;
    }

    const activeDt = activeTab === "passed" ? dtRef2.current : dtRef1.current;
    if (!activeDt) return;

    Swal.fire({
      title: 'Please wait...',
      text: 'Filtering candidates',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => Swal.showLoading()
    });

    activeDt.ajax.reload(() => Swal.close(), false);
  }, [genderFilter, statusFilter, refreshKey, activeTab]);

  const buildColumns = useCallback((tab) => [
    {
      data: null, title: "Sr.No", orderable: false, searchable: false, className: "cl-muted",
      render: (_d, _t, _r, meta) => meta.row + meta.settings._iDisplayStart + 1,
    },
    {
      data: null,
      title: "CandidateNo",
      render: (d, type) => {
        if (type !== "display") {
          return `${d.firstName} ${d.lastName}`;
        }

        return `
          <div class="cl-name-cell">
            <a
              href="/admin/CandiAdd/${d.id}"
              class="cl-id-link"
              onclick="event.preventDefault(); window.open(this.href, '_blank');"
            >
              ${d.id}
            </a>
          </div>
        `;
      },
    },
    {
      data: null, title: "Candidate",
      render: (d, type) => {
        if (type !== "display") return `${d.firstName} ${d.lastName}`;
        const age      = calcAgeInYears(d.dob);
        const ageBadge = age !== null && age < 1
          ? `<span class="cl-badge badge-err" style="font-size:10px;margin-left:4px">⚠ Age &lt;1yr</span>` : "";
        const lockBadge = !isTestEligible(d)
          ? `<span class="cl-badge badge-o" style="font-size:10px;margin-left:4px" title="${daysUntilEligible(d)} days until test-eligible">🔒 ${daysUntilEligible(d)}d</span>` : "";
        return `<div class="cl-name-cell">${d.firstName} ${d.lastName}${ageBadge}${lockBadge}</div>`;
      },
    },
    {
      data: "candidate_img", title: "PI", width: "60px", searchable: false, orderable: false,
      render: (d, type) => {
        if (type !== "display") return "";
        if (!d) return `<span style="font-size:10px">—</span>`;
        return `<img src="${d}" class="candidateIMG" width="38" height="38" style="border-radius:50%;object-fit:cover;display:block;"
                  onerror="this.style.display='none';var b=document.createElement('span');b.className='cl-badge badge-o';b.style.fontSize='10px';b.textContent='No Img';this.parentNode.appendChild(b);"/>`;
      },
    },
    { data: "mobile", title: "Mobile" },
    {
      data: "email", title: "Email",
      render: (d, type) => type === "display" ? `<span>${d}</span>` : d,
    },
    {
      data: "gender",
      title: "Gender",
      render: function (data) {
        if (!data) return "";
        return data.charAt(0).toUpperCase() + data.slice(1).toLowerCase();
      }
    },
    {
      data: "joiningDate",
      title: "Register Date",
      render: (d, type) => {
        if (!d) {
          return type === "display"
            ? `<span class="cl-muted">-</span>`
            : "";
        }
        const date = new Date(d);
        if (type === "display") {
          const formatted = date.toLocaleDateString("en-GB", {
            timeZone: "Asia/Kolkata",
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          });
          return `<span>${formatted.replace(/\//g, "-")}</span>`;
        }
        return date.toISOString();
      },
    },
    {
      data: null, title: "Test",
      render: (d, type) => {
        if (type !== "display") return d.test || "";
        if (!d.test) return `<span style="font-size:12px">No test</span>`;
        return `<span style="font-size:12px">${d.test}</span>`;
      },
    },
    {
      data: "amount", title: "Amount",
      render: (d, type) => {
        if (type !== "display") return d || 0;
        const formatted = formatAmount(d);
        return formatted ? `₹${formatted}` : `<span class="cl-muted">—</span>`;
      },
    },
    {
      // ── Unified "ID Proof" column — data key stays idNumber but display
      // uses row.idType to pick the right label/mask. ─────────────────────
      data: "idNumber", title: "ID Proof", width: "150px",
      render: (d, type, row) => {
        if (type !== "display") return d || "";
        return `<span style="font-size:12px">${idProofDisplay(row.idType, d)}</span>`;
      },
    },
    {
      data: "status", title: "Status",
      render: (d, type) => {
        if (type !== "display") return d || "active";
        const s   = (d || "active").toLowerCase();
        const map = { active: ["badge-ok", "Active"], deactivated: ["badge-err", "Deactivated"], unregistered: ["badge-o", "Unregistered"] };
        const [cls, label] = map[s] || map.active;
        return `<span class="cl-badge ${cls}">${label}</span>`;
      },
    },
    
    ...(tab === "passed"
      ? [{
          data: null, title: "Test result", orderable: false, searchable: false,
          render: (d, type) => {
            if (type !== "display") return "";
            return `<span class="cl-badge badge-ok" style="font-size:11px">✓ Passed</span>`;
          },
        }]
      : []),
      {
        data: null, title: "Action", orderable: false, searchable: false,
        render: (d) => {
          
          let btns  = `<button class="cl-btn cl-btn-ghost cl-btn-sm" data-action="edit" data-id="${d.id}">View</button>`;
          return `<div class="cl-action-btns">${btns}</div>`;
        },
      },
      {
        data: null, title: tab === "passed" ? "Send Reminder Mail" : "Assign Test", orderable: false, searchable: false,
        render: (d) => {
          if (tab === "passed") {
            return `<input type="checkbox" class="${tab === "passed" ? "cl-passed-check" : "cl-row-check"}" data-id="${d.id}"
                      style="cursor:pointer;width:15px;height:15px"/>`;
          }
          if (!isTestEligible(d)) return "";
          return `<input type="checkbox" class="cl-row-check" data-id="${d.id}"
                    style="cursor:pointer;width:15px;height:15px"/>`;
        },
      },
  ], [roleid]);

  const initDt = useCallback((node, dtRef, mountedRef, tab) => {
    const timer = setTimeout(() => {
      if (!node || mountedRef.current) return;
      mountedRef.current = true;

      dtRef.current = $(node).DataTable({
        serverSide: true, processing: true, searchDelay: 500,
        ajax: (dtParams, callback) => {
          axios.get(API.candidates, {
            headers: authHeaders(),
            params: {
              draw:               dtParams.draw,
              start:              dtParams.start,
              length:             dtParams.length === -1 ? 999999 : dtParams.length,
              'search[value]':    dtParams.search?.value || '',
              'order[0][column]': dtParams.order?.[0]?.column ?? 1,
              'order[0][dir]':    dtParams.order?.[0]?.dir    ?? "asc",
              gender:             genderRef.current,
              status:             (statusRef.current === "deactivated" || statusRef.current === "unregistered") ? statusRef.current : "",
              has_test:           statusRef.current === "tests" ? 1 : "",
              tab,
            },
          })
          .then((res) => callback({
            draw:            res.data.draw,
            recordsTotal:    res.data.recordsTotal,
            recordsFiltered: res.data.recordsFiltered,
            data:            (res.data.data || []).map(normaliseRow),
          }))
          .catch(() => callback({ draw: dtParams.draw, recordsTotal: 0, recordsFiltered: 0, data: [] }));
        },
        dom: "Blfrtip", pageLength: 25,
        lengthMenu: [[10, 25, 50, 100, 250, 500, -1], [10, 25, 50, 100, 250, 500, "All"]],
        buttons: [
          { extend: "copyHtml5",  className: "cl-dt-btn", text: "Copy",  exportOptions: { columns: ":not(:last-child)" } },
          { extend: "excelHtml5", className: "cl-dt-btn", text: "Excel", exportOptions: { columns: ":not(:last-child)" } },
          { extend: "csvHtml5",   className: "cl-dt-btn", text: "CSV",   exportOptions: { columns: ":not(:last-child)" } },
          { extend: "pdfHtml5",   className: "cl-dt-btn", text: "PDF", orientation: "landscape", pageSize: "A4", exportOptions: { columns: ":not(:last-child)" } },
          { extend: "print",      className: "cl-dt-btn", text: "Print", exportOptions: { columns: ":not(:last-child)" } },
        ],
        columns: buildColumns(tab),
        order: [[2, "asc"]],
        initComplete() { $(`#${node.id}_length select`).addClass("cl-dt-page-select"); },

        drawCallback() {
          if (tab === "passed") {
            const visible = [];
            dtRef.current.rows({ page: "current" }).every(function () { visible.push(this.data()); });
            allPassedRowsRef.current = visible;

            const current = selectedPassedRowsRef.current;
            $(node).find(".cl-passed-check").each(function () {
              const id = String($(this).data("id"));
              $(this).prop("checked", current.some((r) => String(r.id) === id));
            });

            const allChecked = visible.length > 0 && visible.every((r) =>
              current.some((s) => String(s.id) === String(r.id))
            );
            $("#passed-select-all").prop("checked", allChecked).prop("indeterminate",
              !allChecked && visible.some((r) => current.some((s) => String(s.id) === String(r.id)))
            );
          } else {
            const visible = [];
            dtRef.current.rows({ page: "current" }).every(function () { visible.push(this.data()); });
            allPendingRowsRef.current = visible;

            const current = selectedRowsRef.current;
            $(node).find(".cl-row-check").each(function () {
              const id = String($(this).data("id"));
              $(this).prop("checked", current.some((r) => String(r.id) === id));
            });

            const eligibleVisible = visible.filter((r) => isTestEligible(r));
            const allChecked = eligibleVisible.length > 0 && eligibleVisible.every((r) =>
              current.some((s) => String(s.id) === String(r.id))
            );
            $("#pending-select-all").prop("checked", allChecked).prop("indeterminate",
              !allChecked && eligibleVisible.some((r) => current.some((s) => String(s.id) === String(r.id)))
            );
          }
        },

        rowCallback(row, data) {
          const age = calcAgeInYears(data.dob);
          if (age !== null && age < 1) $(row).css("background", "var(--cl-danger-bg, #fef2f2)");
        },
      });

      $(node).on("change", ".cl-row-check", function () {
        const id      = String($(this).data("id"));
        const checked = $(this).is(":checked");
        if (checked) {
          let row = null;
          dtRef.current.rows().every(function () {
            if (String(this.data().id) === id) { row = this.data(); return false; }
          });
          if (!row) return;
          const next = [...selectedRowsRef.current.filter((r) => String(r.id) !== id), row];
          selectedRowsRef.current = next;
          setSelectedRows(next);
        } else {
          const next = selectedRowsRef.current.filter((r) => String(r.id) !== id);
          selectedRowsRef.current = next;
          setSelectedRows(next);
        }
      });

      $(node).on("change", ".cl-passed-check", function () {
        const id      = String($(this).data("id"));
        const checked = $(this).is(":checked");
        if (checked) {
          let row = null;
          dtRef.current.rows().every(function () {
            if (String(this.data().id) === id) { row = this.data(); return false; }
          });
          if (!row) return;
          const next = [...selectedPassedRowsRef.current.filter((r) => String(r.id) !== id), row];
          selectedPassedRowsRef.current = next;
          setSelectedPassedRows(next);
        } else {
          const next = selectedPassedRowsRef.current.filter((r) => String(r.id) !== id);
          selectedPassedRowsRef.current = next;
          setSelectedPassedRows(next);
        }
      });

      if (tab === "passed") {
        $(document).off("change", "#passed-select-all").on("change", "#passed-select-all", function () {
          if ($(this).is(":checked")) selectAllPassedPage();
          else                        deselectPassedPage();
        });
      } else {
        $(document).off("change", "#pending-select-all").on("change", "#pending-select-all", function () {
          if ($(this).is(":checked")) selectAllPendingPage();
          else                        deselectPendingPage();
        });
      }

      $(node).on("click", "[data-action]", function (e) {
        // Anchors (the CandidateNo id link) carry a real href now — let
        // ctrl/cmd-click, shift-click and middle-click fall through to the
        // browser's native "open in new tab" / "open in new window" behaviour
        // instead of being hijacked into a SPA navigate(). The plain "View"
        // button has no href, so this only affects the id link.
        if (this.tagName === "A" && (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1)) {
          return;
        }

        e.preventDefault();
        const action = $(this).data("action");
        const id     = String($(this).data("id"));
        let row = null;
        dtRef.current.rows().every(function () { if (String(this.data().id) === id) { row = this.data(); return false; } });
        if (!row) return;
        if      (action === "edit")       onEditRef.current(row);
        else if (action === "unregister") onUnregisterRef.current(row);
        else if (action === "deactivate") onDeactivateRef.current(row);
        else if (action === "delete")     onDeleteRef.current(row);
      });
    }, 0);
    return () => clearTimeout(timer);
  }, [buildColumns]);

  useEffect(() => {
    const node = tableRef1.current;
    return initDt(node, dtRef1, mounted1, "pending");
  }, [initDt]);

  useEffect(() => {
    const node = tableRef2.current;
    return initDt(node, dtRef2, mounted2, "passed");
  }, [initDt]);

  useEffect(() => {
    return () => {
      [tableRef1, tableRef2].forEach((ref, i) => {
        const node  = ref.current;
        const dtRef = i === 0 ? dtRef1 : dtRef2;
        if (node && $.fn.dataTable.isDataTable(node)) {
          $(node).off("click",  "[data-action]");
          $(node).off("change", ".cl-row-check");
          $(node).DataTable().destroy(true);
          dtRef.current = null;
        }
      });
      mounted1.current = false;
      mounted2.current = false;
    };
  }, []);

  const clearAllSelections = useCallback(() => {
    selectedRowsRef.current = [];
    setSelectedRows([]);
    $(".cl-row-check").prop("checked", false);
    $("#pending-select-all").prop("checked", false).prop("indeterminate", false);
  }, []);

  const clearPassedSelections = useCallback(() => {
    selectedPassedRowsRef.current = [];
    setSelectedPassedRows([]);
    $(".cl-passed-check").prop("checked", false);
    $("#passed-select-all").prop("checked", false).prop("indeterminate", false);
  }, []);

  const selectAllPendingPage = useCallback(() => {
    const eligible = allPendingRowsRef.current.filter((r) => isTestEligible(r));
    const merged   = [...selectedRowsRef.current];
    eligible.forEach((r) => {
      if (!merged.some((s) => String(s.id) === String(r.id))) merged.push(r);
    });
    selectedRowsRef.current = merged;
    setSelectedRows([...merged]);
    $(".cl-row-check").prop("checked", true);
    $("#pending-select-all").prop("checked", true).prop("indeterminate", false);
  }, []);

  const deselectPendingPage = useCallback(() => {
    const visibleIds = new Set(allPendingRowsRef.current.map((r) => String(r.id)));
    const next = selectedRowsRef.current.filter((r) => !visibleIds.has(String(r.id)));
    selectedRowsRef.current = next;
    setSelectedRows([...next]);
    $(".cl-row-check").prop("checked", false);
    $("#pending-select-all").prop("checked", false).prop("indeterminate", false);
  }, []);

  const selectAllPassedPage = useCallback(() => {
    const visible = allPassedRowsRef.current;
    const merged  = [...selectedPassedRowsRef.current];
    visible.forEach((r) => {
      if (!merged.some((s) => String(s.id) === String(r.id))) merged.push(r);
    });
    selectedPassedRowsRef.current = merged;
    setSelectedPassedRows([...merged]);
    $(".cl-passed-check").prop("checked", true);
    $("#passed-select-all").prop("checked", true).prop("indeterminate", false);
  }, []);

  const deselectPassedPage = useCallback(() => {
    const visibleIds = new Set(allPassedRowsRef.current.map((r) => String(r.id)));
    const next = selectedPassedRowsRef.current.filter((r) => !visibleIds.has(String(r.id)));
    selectedPassedRowsRef.current = next;
    setSelectedPassedRows([...next]);
    $(".cl-passed-check").prop("checked", false);
    $("#passed-select-all").prop("checked", false).prop("indeterminate", false);
  }, []);

  const ineligibleSelected = selectedRows.filter((r) => !isTestEligible(r));

  return (
    <>
      <div className="cl-topbar">
        <h1 className="cl-page-title">Candidates</h1>
        <div className="cl-topbar-actions">
          {selectedPassedRows.length > 0 && (
            <button
              className="cl-btn cl-btn-primary"
              onClick={() => setShowMailConfirm(true)}
              style={{ background: "var(--cl-info,#3b82f6)", borderColor: "var(--cl-info,#3b82f6)" }}
            >
              ✉️ Send Joining Mail to {selectedPassedRows.length} Selected
            </button>
          )}
          {selectedRows.length > 0 && (
            <button
              className="cl-btn cl-btn-primary"
              onClick={() => setShowAssignModal(true)}
              style={{ background: "var(--cl-success,#10b981)", borderColor: "var(--cl-success,#10b981)" }}
            >
              🎯 Assign Test to {selectedRows.length} Selected
              {ineligibleSelected.length > 0 && (
                <span style={{ marginLeft: 6, fontSize: 11, opacity: 0.85 }}>
                  ({ineligibleSelected.length} ineligible)
                </span>
              )}
            </button>
          )}
          <button className="cl-btn cl-btn-ghost" onClick={onBulk}>
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M2 12h12M8 2v8M5 6l3-4 3 4"/></svg>
            Bulk Upload
          </button>
          <button className="cl-btn cl-btn-primary" onClick={onAdd}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 1v10M1 6h10"/></svg>
            Add Candidate
          </button>
        </div>
      </div>

      {selectedRows.length > 0 && (
        <div className="cl-filter-pill" style={{ background: "var(--cl-success-bg,#f0fdf4)", borderColor: "var(--cl-success,#10b981)" }}>
          <span style={{ color: "var(--cl-success,#10b981)", fontWeight: 600 }}>
            ✓ {selectedRows.length} candidate{selectedRows.length !== 1 ? "s" : ""} selected:
          </span>
          {selectedRows.slice(0, 5).map((r) => (
            <strong key={r.id} className="cl-filter-pill-val" style={{ marginLeft: 4 }}>
              {r.firstName} {r.lastName}
            </strong>
          ))}
          {selectedRows.length > 5 && <span> +{selectedRows.length - 5} more</span>}
          <button style={{ padding: 5, borderRadius: 10 }} className="cl-filter-clear"
            onClick={clearAllSelections}>
            ✕ Clear
          </button>
        </div>
      )}

      {selectedPassedRows.length > 0 && (
        <div className="cl-filter-pill" style={{ background: "var(--cl-info-bg,#eff6ff)", borderColor: "var(--cl-info,#3b82f6)" }}>
          <span style={{ color: "var(--cl-info,#3b82f6)", fontWeight: 600 }}>
            ✉️ {selectedPassedRows.length} candidate{selectedPassedRows.length !== 1 ? "s" : ""} selected for joining mail:
          </span>
          {selectedPassedRows.slice(0, 5).map((r) => (
            <strong key={r.id} className="cl-filter-pill-val" style={{ marginLeft: 4 }}>
              {r.firstName} {r.lastName}
            </strong>
          ))}
          {selectedPassedRows.length > 5 && <span> +{selectedPassedRows.length - 5} more</span>}
          <button style={{ padding: 5, borderRadius: 10 }} className="cl-filter-clear"
            onClick={clearPassedSelections}>
            ✕ Clear
          </button>
        </div>
      )}

      <div className="cl-stats-row">
        <StatCard label="Total"        value={statsLoading ? "…" : stats.total}        active={!genderFilter && !statusFilter}           onClick={() => { setGenderFilter(""); setStatusFilter(""); }}/>
        <StatCard label="Male"         value={statsLoading ? "…" : stats.male}         active={genderFilter === "male"}   onClick={() => setGenderFilter(genderFilter === "male"   ? "" : "male")}/>
        <StatCard label="Female"       value={statsLoading ? "…" : stats.female}       active={genderFilter === "female"} onClick={() => setGenderFilter(genderFilter === "female" ? "" : "female")}/>
        <StatCard label="Tests"        value={statsLoading ? "…" : stats.tests}        active={statusFilter === "tests"}        onClick={() => setStatusFilter(statusFilter === "tests" ? "" : "tests")}/>
        <StatCard label="Deactivated"  value={statsLoading ? "…" : stats.deactivated}  active={statusFilter === "deactivated"}  onClick={() => setStatusFilter(statusFilter === "deactivated" ? "" : "deactivated")}/>
        <StatCard label="Unregistered" value={statsLoading ? "…" : stats.unregistered} active={statusFilter === "unregistered"} onClick={() => setStatusFilter(statusFilter === "unregistered" ? "" : "unregistered")}/>
      </div>

      {genderFilter && (
        <div className="cl-filter-pill">
          Showing: <strong className="cl-filter-pill-val">{genderFilter}</strong>
          <button className="cl-filter-clear" onClick={() => setGenderFilter("")}>✕ Clear</button>
        </div>
      )}

      {statusFilter && (
        <div className="cl-filter-pill">
          Showing: <strong className="cl-filter-pill-val">
            {statusFilter === "tests" ? "Has test assigned" : statusFilter === "deactivated" ? "Deactivated" : "Unregistered"}
          </strong>
          <button className="cl-filter-clear" onClick={() => setStatusFilter("")}>✕ Clear</button>
        </div>
      )}

      <div className="cl-tab-switch" style={{
        display: "flex", gap: 4, padding: "4px",
        background: "var(--cl-th-bg,#f3f4f6)",
        borderRadius: 10, width: "fit-content", margin: "12px 0 0",
      }}>
        {[
          { key: "pending", icon: "👤", label: "Candidates",        sub: "No test / pending" },
          { key: "passed",  icon: "🏆", label: "Passed — not joined", sub: "Test cleared, awaiting membership" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => {
              setActiveTab(t.key);
              setShowPriorEvidence(false);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 18px",
              borderRadius: 8,
              border: "none",
              cursor: "pointer",
              background: activeTab === t.key ? "var(--cl-card-bg,#fff)" : "transparent",
              boxShadow: activeTab === t.key ? "0 1px 4px rgba(0,0,0,0.10)" : "none",
              fontWeight: activeTab === t.key ? 600 : 400,
              color: activeTab === t.key ? "var(--cl-primary,#6366f1)" : "var(--cl-label,#888)",
              fontSize: 13,
              transition: "all 0.18s",
            }}
          >
            <span>{t.icon}</span>
            <div style={{ textAlign: "left" }}>
              <div>{t.label}</div>
              <div style={{ fontSize: 10, opacity: 0.7, fontWeight: 400 }}>
                {t.sub}
              </div>
            </div>
          </button>
        ))}
       <button
          className="cl-btn cl-btn-ghost prior-btn"
          onClick={() => {
            setShowPriorEvidence(true);
            setActiveTab(null);
          }}
          style={{
            background: showPriorEvidence
              ? "var(--cl-card-bg,#fff)"
              : "transparent",
            boxShadow: showPriorEvidence
              ? "0 1px 4px rgba(0,0,0,0.10)"
              : "none",
            fontWeight: showPriorEvidence ? 600 : 400,
            color: showPriorEvidence
              ? "var(--cl-primary,#6366f1)"
              : "var(--cl-label,#888)",
          }}
        >
          🏆 Prior Evidence — Passed, Not Joined
        </button>
      </div>
            
        <div className="profile-section" style={{ display: activeTab === "pending" ? "block" : "none" }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "8px 12px", background: "var(--cl-th-bg,#f3f4f6)",
            borderRadius: "8px 8px 0 0", borderBottom: "1px solid var(--cl-border)",
            fontSize: 12, fontWeight: 600, color: "var(--cl-label)",
            letterSpacing: "0.06em",
          }}>
            <span>👤</span> Candidates — no test assigned / test pending
          </div>
          <div className="table-responsive"><br/>
            <table id="candidatelist" ref={tableRef1} className="table table-bordered table-striped" style={{ width: "100%" }}>
              <thead>
                <tr>
                  <th></th><th>Sr.No</th><th>Candidate</th><th>PI</th>
                  <th>Mobile</th><th>Email</th><th>Gender</th>
                  <th>Test</th><th>Amount</th><th>ID Proof</th><th>Status</th><th></th>
                </tr>
              </thead>
            </table>
          </div>
        </div>
        <div className="profile-section" style={{ display: activeTab === "passed" ? "block" : "none", marginTop: 0 }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "8px 12px", background: "var(--cl-success-bg,#f0fdf4)",
            borderRadius: "8px 8px 0 0", borderBottom: "1px solid var(--cl-success,#10b981)",
            fontSize: 12, fontWeight: 600, color: "var(--cl-success,#10b981)",
            letterSpacing: "0.06em",
          }}>
            <span>🏆</span> Passed test — not yet a member
          </div>
          <div className="table-responsive"><br/>
            <table id="candidatelist_passed" ref={tableRef2} className="table table-bordered table-striped" style={{ width: "100%" }}>
              <thead>
                <tr>
                  <th></th><th>Sr.No</th><th>Candidate</th><th>PI</th>
                  <th>Mobile</th><th>Email</th><th>Gender</th>
                  <th>Test</th><th>Amount</th><th>ID Proof</th><th>Status</th>
                  <th>Test result</th><th></th>
                </tr>
              </thead>
            </table>
          </div>
        </div>
      
      {!showPriorEvidence && (
        <IneligiblePanel onReload={() => {
          if (dtRef1.current) dtRef1.current.ajax.reload(null, false);
          if (dtRef2.current) dtRef2.current.ajax.reload(null, false);
        }}/>
      )}
      

      {showPriorEvidence && (
        <WidgetErrorBoundary>
          <PassNotJoinedPanel/>
        </WidgetErrorBoundary>
      )}

      <ConfirmModal
        open={showMailConfirm}
        title="✉️ Send Joining Mail"
        message={
          <div>
            <p style={{ marginBottom: 8 }}>
              Send joining mail to <strong>{selectedPassedRows.length}</strong> candidate{selectedPassedRows.length !== 1 ? "s" : ""}?
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {selectedPassedRows.slice(0, 8).map((r) => (
                <span key={r.id} className="cl-badge badge-ok" style={{ fontSize: 11 }}>
                  {r.firstName} {r.lastName}
                </span>
              ))}
              {selectedPassedRows.length > 8 && (
                <span className="cl-badge badge-o" style={{ fontSize: 11 }}>+{selectedPassedRows.length - 8} more</span>
              )}
            </div>
          </div>
        }
        confirmLabel={mailSending ? "⏳ Sending…" : "✉️ Send Mail"}
        confirmClass="cl-btn-primary"
        onCancel={() => setShowMailConfirm(false)}
        onConfirm={async () => {
          setMailSending(true);
          try {
            await axios.post(
              `${baseurl}/admin/candidates/send_joining_mail`,
              { ids: selectedPassedRows.map((r) => r.id) },
              { headers: authHeaders() }
            );
            onAssignSuccess(
              `✓ Joining mail sent to ${selectedPassedRows.length} candidate${selectedPassedRows.length !== 1 ? "s" : ""}!`
            );
            clearPassedSelections();
            setShowMailConfirm(false);
            window.location.reload();
          } catch (e) {
            onAssignSuccess(e.response?.data?.error || e.message || "Failed to send mail");
          } finally {
            setMailSending(false);
          }
        }}
      />

      <AssignTestModal
        open={showAssignModal}
        candidates={selectedRows}
        tests={tests}
        onClose={() => setShowAssignModal(false)}
        onSuccess={(msg) => {
          onAssignSuccess(msg);
          clearAllSelections();
          if (dtRef1.current) dtRef1.current.ajax.reload(null, false);
          if (dtRef2.current) dtRef2.current.ajax.reload(null, false);
          setShowAssignModal(false);
        }}
      />
    </>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// PRIOR EVIDENCE — PASSED, NOT JOINED PANEL
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// PRIOR EVIDENCE — PASSED, NOT JOINED PANEL
// ─────────────────────────────────────────────────────────────────────────────
function PassNotJoinedPanel() {
  const [rows,          setRows]          = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState("");
  const [selectedCount, setSelectedCount] = useState(0);
  const [showMailConfirm, setShowMailConfirm] = useState(false);
  const [mailSending,     setMailSending]     = useState(false);

  const tableRef       = useRef(null);
  const dtRef          = useRef(null);
  const selectedIdsRef = useRef(new Set());

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const token = sessionStorage.getItem("token");
      if (!token) { setLoading(false); return; }

      const response = await fetch(`${baseurl}/admin/priorevidence`, {
        method: "GET",
        headers: { Authorization: token, "Content-Type": "application/json" },
      });
      if (!response.ok) throw new Error("Network response was not ok");
      const data = await response.json();

      const passnotjoin = (data.passnotjoin || []).map((t) => ({
        ...t,
        prior_evidence_date_time: formatDateTime(t.prior_evidence_date_time),
      }));
      setRows(passnotjoin);
    } catch (e) {
      console.error("Error fetching prior evidence:", e);
      setError(e.message || "Failed to load prior evidence");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const node = tableRef.current;
    if (!node) return;

    if (dtRef.current) {
      dtRef.current.destroy();
      dtRef.current = null;
      $(node).empty();
    }

    // Reset selection whenever the underlying data changes
    selectedIdsRef.current = new Set();
    setSelectedCount(0);

    if (!rows.length) return;

    const columns = [
      {
        data: null, title: "Sr.No", orderable: false, searchable: false,
        render: (_d, _t, _r, meta) => meta.row + meta.settings._iDisplayStart + 1,
      },
      { data: "prior_evidence_date_time", title: "Date" },
      {
        data: null, title: "Name",
        render: (_d, type, row) => {
          if (type !== "display") return `${row.first_name || ""} ${row.last_name || ""}`;
          return `<span style="font-weight:600">${row.first_name || ""} ${row.last_name || ""}</span>`;
        },
      },
      { data: "mobile_no", title: "Mobile No" },
      { data: "email",     title: "Email" },
      {
        data: null, title: "Result",
        render: (_d, type, row) => {
          if (type !== "display") return row.prior_exam_result || "Pass";
          return `<span class="cl-badge badge-ok" style="font-size:12px">✓ Pass</span>`;
        },
      },
      {
        data: null, title: "Send Joining Mail", orderable: false, searchable: false,
        render: (_d, type, row) => {
          if (type !== "display") return "";
          const id = row.test_candidate_id || row.pre_test_candidate_id || "";
          return `<div style="text-align:center">
                    <input
                      type="checkbox"
                      class="joining-mail-checkbox"
                      data-id="${id}"
                      data-email="${row.email || ""}"
                      style="width:16px;height:16px;cursor:pointer"
                    />
                  </div>`;
        },
      },
    ];

    dtRef.current = $(node).DataTable({
      data: rows,
      columns,
      dom: "Blfrtip",
      pageLength: 10,
      lengthMenu: [[10, 25, 50, -1], [10, 25, 50, "All"]],
      buttons: [
        {
          extend: "copyHtml5", className: "cl-dt-btn", text: "Copy",
          exportOptions: {
            columns: ":not(:last-child)",
            format: {
              body:   (d) => (d == null ? "" : d.toString().replace(/<[^>]*>/g, "").trim()),
              header: (d) => (!d ? "" : d.toString().replace(/<[^>]*>/g, "").trim()),
            },
          },
        },
        {
          extend: "excelHtml5", className: "cl-dt-btn", text: "Excel",
          exportOptions: {
            columns: ":not(:last-child)",
            format: {
              body:   (d) => (d == null ? "" : d.toString().replace(/<[^>]*>/g, "").trim()),
              header: (d) => (!d ? "" : d.toString().replace(/<[^>]*>/g, "").trim()),
            },
          },
        },
        {
          extend: "csvHtml5", className: "cl-dt-btn", text: "CSV",
          exportOptions: {
            columns: ":not(:last-child)",
            format: {
              body:   (d) => (d == null ? "" : d.toString().replace(/<[^>]*>/g, "").trim()),
              header: (d) => (!d ? "" : d.toString().replace(/<[^>]*>/g, "").trim()),
            },
          },
        },
        {
          extend: "pdfHtml5", className: "cl-dt-btn", text: "PDF",
          orientation: "landscape", pageSize: "A4",
          exportOptions: {
            columns: ":not(:last-child)",
            format: {
              body:   (d) => (d == null ? "" : d.toString().replace(/<[^>]*>/g, "").trim()),
              header: (d) => (!d ? "" : d.toString().replace(/<[^>]*>/g, "").trim()),
            },
          },
        },
        {
          extend: "print", className: "cl-dt-btn", text: "Print",
          exportOptions: {
            columns: ":not(:last-child)",
            format: {
              body:   (d) => (d == null ? "" : d.toString().replace(/<[^>]*>/g, "").trim()),
              header: (d) => (!d ? "" : d.toString().replace(/<[^>]*>/g, "").trim()),
            },
          },
        },
      ],
      order: [[0, "asc"]],
      // Highlight all rows amber — they are pending action
      rowCallback: function (row) {
        $(row).css("background-color", "#fffbeb");
      },
      initComplete: function () {
        $(node).closest(".dataTables_wrapper").find(".dt-length select").addClass("cl-dt-page-select");

        // "Select all" in the header — last column
        const headerCells = $(node).find("thead tr th");
        headerCells.eq(headerCells.length - 1).html(
          '<div style="text-align:center"><input type="checkbox" id="selectAllJoiningMail" style="width:15px;height:15px;cursor:pointer"> Send Joining Mail</div>'
        );
      },
      destroy: true,
    });

    // Per-row checkbox tracking
    $(node).off("change", ".joining-mail-checkbox").on("change", ".joining-mail-checkbox", function () {
      const id = String($(this).data("id"));
      if (!id) return;
      if ($(this).is(":checked")) selectedIdsRef.current.add(id);
      else                        selectedIdsRef.current.delete(id);
      setSelectedCount(selectedIdsRef.current.size);
    });

    // Select-all checkbox
    $(document).off("change", "#selectAllJoiningMail").on("change", "#selectAllJoiningMail", function () {
      const checked = $(this).is(":checked");
      $(node).find(".joining-mail-checkbox").prop("checked", checked).trigger("change");
    });

    return () => {
      if (dtRef.current) {
        dtRef.current.destroy();
        dtRef.current = null;
      }
    };
  }, [rows]);

  useEffect(() => {
    return () => {
      if (dtRef.current) {
        dtRef.current.destroy();
        dtRef.current = null;
      }
    };
  }, []);

  const sendJoiningMail = async () => {
    const ids = [...selectedIdsRef.current];
    if (!ids.length) return;
    setMailSending(true);
    try {
      await axios.post(
        `${baseurl}/admin/candidates/send_joining_mail`,
        { ids },
        { headers: authHeaders() }
      );
      setShowMailConfirm(false);
      selectedIdsRef.current = new Set();
      setSelectedCount(0);
      $(tableRef.current).find(".joining-mail-checkbox").prop("checked", false);
      $("#selectAllJoiningMail").prop("checked", false);
      load();
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Failed to send mail");
    } finally {
      setMailSending(false);
    }
  };

  return (
    <div className="profile-section" style={{ marginTop: 24 }}>
      <div style={{
        display:"flex", alignItems:"center", justifyContent:"space-between",
        background:"var(--cl-success-bg,#f0fdf4)", border:"1px solid var(--cl-success,#10b981)",
        borderRadius:10, padding:"10px 16px", marginBottom:12,
      }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <span style={{ fontSize:22 }}>🏆</span>
          <div>
            <div style={{ fontWeight:700, color:"var(--cl-success,#10b981)", fontSize:14 }}>
              Prior Evidence — Passed, Not Joined
            </div>
            <div className="cl-muted" style={{ fontSize:12 }}>
              {loading
                ? "Loading…"
                : `${rows.length} candidate${rows.length !== 1 ? "s" : ""} passed a prior-evidence test and haven't joined yet.`}
            </div>
          </div>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          {selectedCount > 0 && (
            <button
              className="cl-btn cl-btn-primary"
              style={{ background:"var(--cl-info,#3b82f6)", borderColor:"var(--cl-info,#3b82f6)" }}
              onClick={() => setShowMailConfirm(true)}
            >
              ✉️ Send Joining Mail to {selectedCount} Selected
            </button>
          )}
          <button className="cl-btn cl-btn-ghost cl-btn-sm" onClick={load}>↺ Refresh</button>
        </div>
      </div>

      {error && <InlineError msg={error} />}

      {!loading && rows.length === 0 && !error && (
        <div className="cl-muted" style={{ padding: "12px 4px", fontSize: 13 }}>
          No pass-not-joined candidates found.
        </div>
      )}

      <div className="table-responsive" style={{ display: rows.length ? "block" : "none" }}>
        <table
          id="priorEvidencePassNotJoin"
          ref={tableRef}
          className="table table-bordered table-striped"
          style={{ width: "100%" }}
        >
          <thead></thead>
          <tbody></tbody>
        </table>
      </div>

      <ConfirmModal
        open={showMailConfirm}
        title="✉️ Send Joining Mail"
        message={`Send joining mail to ${selectedCount} candidate${selectedCount !== 1 ? "s" : ""}?`}
        confirmLabel={mailSending ? "⏳ Sending…" : "✉️ Send Mail"}
        confirmClass="cl-btn-primary"
        onCancel={() => setShowMailConfirm(false)}
        onConfirm={sendJoiningMail}
      />
    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────────
// ROOT COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function CandidateList() {
  const location = useLocation();
  // Opened from a Batch Dashboard: candidates are added as institute candidates of that batch
  const batchCtx = location.state?.batchCtx || null;
  const [view,         setView]         = useState(batchCtx?.view || "list");
  const [tests,        setTests]        = useState([]);
  const [selected,     setSelected]     = useState(null);
  const [genderFilter, setGenderFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [toast,        setToast]        = useState({msg:"",type:""});
  const [listKey,      setListKey]      = useState(0);
  const [refreshKey,   setRefreshKey]   = useState(0);
  const [stats,        setStats]        = useState({total:0,male:0,female:0,tests:0,deactivated:0,unregistered:0});
  const [statsLoading, setStatsLoading] = useState(true);
  const [confirm,      setConfirm]      = useState({open:false,type:"",candidate:null});
  const navigate = useNavigate();
  const { candidateId } = useParams();
  const showToast    = useCallback((msg,type="success")=>setToast({msg,type}),[]);
  const dismissToast = useCallback(()=>setToast({msg:"",type:""}),[]);

  const fetchStats = useCallback(async ()=>{
    setStatsLoading(true);
    try { const res=await axios.get(API.stats,{headers:authHeaders()}); setStats(res.data); }
    catch(e){ console.error("Stats fetch failed",e); }
    finally{ setStatsLoading(false); }
  },[]);

  const refreshSelected = useCallback(async (id) => {
    try {
      const res = await axios.get(API.candidate(id), { headers: authHeaders() });
      setSelected(normaliseRow(res.data));
    } catch (e) {
      console.error("Failed to refresh candidate:", e);
    }
  }, []);

  useEffect(() => {
     if (!candidateId) return;
    axios.get(API.candidate(candidateId), { headers: authHeaders() })
      .then((res) => {
        setSelected(normaliseRow(res.data));
        setView("edit");
      })
      .catch((e) => console.error("Candidate fetch failed:", e));
  }, [candidateId]);

  useEffect(()=>{
    fetchStats();
    const req = batchCtx
      ? axios.get(API.tests, { params: { institute: 1, batchId: batchCtx.batchId }, headers: authHeaders() })
      : axios.get(API.tests,{headers:authHeaders()});
    // Super Admin can also assign institute tests from the main candidate list.
    const instituteReq = !batchCtx && getRoleId() === 1
      ? axios.get(API.instituteTests, { headers: authHeaders() }).then((r) => normaliseInstituteTests(r.data)).catch(() => [])
      : Promise.resolve([]);
    Promise.all([req, instituteReq])
      .then(([r, instituteTests]) => setTests([...normaliseTests(r.data), ...instituteTests]))
      .catch(()=>{});
  },[fetchStats, batchCtx?.batchId]); // eslint-disable-line react-hooks/exhaustive-deps

  const goBack = useCallback(()=>{
    setView("list"); 
    setSelected(null);
    setListKey((k)=>k+1);
    fetchStats();
    if (batchCtx) navigate("/admin/BatchDashboard", { state: { batchId: batchCtx.batchId } });
    else navigate("/admin/CandiAdd");
  },[fetchStats, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSuccess    = useCallback((msg)=>{ showToast(msg,"success"); fetchStats(); setRefreshKey((k)=>k+1); },[showToast,fetchStats]);
  const handleEdit       = useCallback((row)=>{ setSelected(row); setView("edit"); navigate(`/admin/CandiAdd/${row.id}`); },[navigate]);
  const askConfirm       = useCallback((type,candidate)=>setConfirm({open:true,type,candidate}),[]);
  const handleUnregister = useCallback((row)=>askConfirm("unregister",row),[askConfirm]);
  const handleDeactivate = useCallback((row)=>askConfirm("deactivate",row),[askConfirm]);
  const handleDelete     = useCallback((row)=>askConfirm("delete",row),[askConfirm]);

  const handleConfirmAction = useCallback(async ()=>{
    const {type,candidate}=confirm;
    setConfirm({open:false,type:"",candidate:null});
    if(!candidate) return;
    try {
      if     (type==="unregister"){ await axios.patch(API.unregister(candidate.id),{},{headers:authHeaders()}); 
      showToast("✓ Candidate unregistered","success"); 
      if (selected?.id===candidate.id) await refreshSelected(candidate.id);
      } else if(type==="deactivate"){ await axios.patch(API.deactivate(candidate.id),{},{headers:authHeaders()}); 
      showToast("✓ Candidate deactivated","success"); 
      if (selected?.id===candidate.id) await refreshSelected(candidate.id);
      } else if(type==="delete")    { await axios.delete(API.candidate(candidate.id),  {headers:authHeaders()}); 
      showToast("✓ Candidate deleted","success"); if(selected?.id===candidate.id) goBack(); 
      if (selected?.id===candidate.id) goBack();
      }
      fetchStats();
      setRefreshKey((k)=>k+1);
    } catch(e){ showToast(e.response?.data?.error||e.message||"Action failed","error"); }
  },[confirm,showToast,fetchStats,selected,goBack,refreshSelected]);

  const cancelConfirm = useCallback(()=>setConfirm({open:false,type:"",candidate:null}),[]);

  const confirmConfig={
    unregister:{title:"Unregister Candidate",message:`Remove ${confirm.candidate?.firstName??""} ${confirm.candidate?.lastName??""} from their Unregistered test?`,confirmLabel:"↩ Unregister",confirmClass:"cl-btn-warning"},
    deactivate:{title:"Deactivate Candidate",message:`Deactivate ${confirm.candidate?.firstName??""} ${confirm.candidate?.lastName??""}? They will no longer be able to access the platform.`,confirmLabel:"⊘ Deactivate",confirmClass:"cl-btn-warning"},
    delete:    {title:"Delete Candidate",    message:`Soft delete ${confirm.candidate?.firstName??""} ${confirm.candidate?.lastName??""}? This cannot be undone.`,confirmLabel:"🗑 Delete",confirmClass:"cl-btn-danger"},
  };
  const cfg=confirmConfig[confirm.type]||{};

  return (
    <div className="cl-root">
      <Toast message={toast.msg} type={toast.type} onClose={dismissToast}/>
      <ConfirmModal open={confirm.open} title={cfg.title} message={cfg.message} confirmLabel={cfg.confirmLabel} confirmClass={cfg.confirmClass} onConfirm={handleConfirmAction} onCancel={cancelConfirm}/>

      {view==="list"&&(
        <ListView
          key={listKey}
          statsLoading={statsLoading} stats={stats}
          genderFilter={genderFilter} setGenderFilter={setGenderFilter}
          statusFilter={statusFilter} setStatusFilter={setStatusFilter}
          refreshKey={refreshKey}
          tests={tests}
          onAdd={()=>setView("add")} onBulk={()=>setView("bulk")}
          onEdit={handleEdit} onUnregister={handleUnregister}
          onDeactivate={handleDeactivate} onDelete={handleDelete}
          onAssignSuccess={handleSuccess}
        />
      )}
      {batchCtx && (
        <div className="container mt-3">
          <div className="alert alert-info mb-0">
            Institute batch mode — candidates you add here are saved as institute candidates of batch #{batchCtx.batchId}.
          </div>
        </div>
      )}
      {view==="add"  &&<AddView  onSuccess={handleSuccess} onBack={goBack} batchCtx={batchCtx}/>}
      {view==="edit" &&<EditView candidate={selected} tests={tests} onSuccess={handleSuccess} onBack={goBack} 
      onDeactivate={handleDeactivate} onDelete={handleDelete} onUnregister={handleUnregister}/>}
      {view==="bulk" &&<BulkView tests={tests} onSuccess={handleSuccess} onBack={goBack} batchCtx={batchCtx}/>}
    </div>
  );
}